import React, { ChangeEvent, useState, useEffect, useRef, useMemo } from "react";
import { EmployeeAcaSummary } from "@/components/aca/EmployeeAcaSummary";
import { EMPLOYMENT_TYPES, PAY_CYCLES, COMPENSATION_TYPES, EMPLOYEE_STATUSES, normalizeEmploymentValue } from "@shared/constants/employment";
import { useParams, useLocation } from "wouter";
import { PendingEnrollmentRequestCard } from "@/components/benefits/PendingEnrollmentRequestCard";
import { isEnrollmentRequestTask } from "@/lib/enrollmentTasks";
import { useQueryState, parseAsStringLiteral } from "nuqs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeft,
  User,
  Building2,
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  ChevronRight,
  ChevronLeft,
  HeartHandshake,
  Users,
  FileText,
  Clock,
  Phone,
  Mail,
  AlertCircle,
  AlertTriangle,
  Heart,
  Eye,
  EyeOff,
  Shield,
  Umbrella,
  Smile,
  DollarSign,
  Edit,
  KeyRound,
  Calendar,
  MapPin,
  Briefcase,
  IdCard,
  CreditCard,
  CheckCircle,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { PermissionGate } from "@/components/ui/permission-gate";
import { usePermissions } from "@/hooks/usePermissions";
import { CompensationHistoryCard } from "@/components/employees/CompensationHistoryCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormField } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { ProfilePictureUpload } from "@/components/ui/profile-picture-upload";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { EnrollmentBadges } from "@/components/ui/benefits/EnrollmentBadges";
import { DeductionSummaryContent } from "@/components/ui/benefits/DeductionSummaryContent";
import { enrollmentEmployeeMonthly, totalPricedMonthly } from "@/lib/planPriceDisplay";
import { filterActiveEnrollments, filterWaivedEnrollments, isActiveDependentEnrollmentForPlan, isCurrentlyEffectiveEnrollment, isPendingEnrollment } from "@/lib/enrollmentFilters";

import { OUTCOME_LABELS, formatDateDisplay, formatDateLong, getCurrentLocalDateTime, parseDateLocal } from "@/lib/utils";
import {
  formatStatus,
  EMPLOYEE_STATUS_OPTIONS,
  normalizeStatus,
} from "@/lib/employeeStatus";
import { formatPhoneInputValue } from "@/lib/phone";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
// TODO: EmployeeAdminAccess component does not exist yet (added by origin/dev)
// import { EmployeeAdminAccess } from "@/components/employer/EmployeeAdminAccess";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useDocuments } from "@/hooks/useDocuments";
import { DocumentList } from "@/components/ui/document-list";
import { DocumentUpload } from "@/components/ui/document-upload";
import { formatBenefitAmount } from "@shared/utils/enrollmentCalculator";
import { SsnInput } from "@/components/employees/SsnInput";
import { fetchFullSsns, ssnToSave } from "@/lib/ssnReveal";
import { isLifeDisabilityType } from "@shared/schemas/plans";
import { isFsaPlanType } from "@shared/schemas/planTypes";
import { resolveNeedsConfigReason } from "@/components/benefits/v2/needsConfig";
import {
  formatPremiumWithPayCycle,
  getPayCycleLabel,
  getPayCycleLabelCapitalized,
  calculatePerPaycheckAmount,
  hasNonMonthlyPayCycle,
} from "@/lib/payrollUtils";
import { queryClient } from "@/lib/queryClient";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { determineCoverageTierFromDependents } from "@/lib/enrollmentFilters";
import { AddressFields } from "@/components/forms/AddressFields";
import { EmergencyContactModal } from "@/components/forms/EmergencyContactModal";
import {
  useEmploymentChangeVerification,
  detectEmploymentChanges,
  type VerificationResult,
} from "@/components/ui/EmploymentChangeVerificationModal";
import {
  composeAddressLine,
  emptyAddressFields,
  normalizeAddressFields,
} from "@/lib/address";
import {
  insertEmployeeSchema,
  insertDependentSchema,
  contactInteractionFormSchema,
  type Employee,
  type EmergencyContact,
  type Dependent,
  type Client,
  type Plan,
  type Enrollment,
  type ContactInteraction,
  type InsertEmployee,
  type InsertDependent,
} from "@shared/schema";
import { DependentsList } from "@/components/dependents/DependentsList";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { TerminateEmployeeModal } from "@/components/employee/TerminateEmployeeModal";
import { BenefitsConfigurationForm } from "@/components/benefits/BenefitsConfigurationForm";
import { CoverageHeaderBanner } from "@/components/benefits/v2/CoverageHeaderBanner";
import { BenefitSection } from "@/components/benefits/v2/BenefitSection";
import { ActiveBenefitRow } from "@/components/benefits/v2/ActiveBenefitRow";
import { WaivedBenefitRow } from "@/components/benefits/v2/WaivedBenefitRow";
import { EmptyTypeRow } from "@/components/benefits/v2/EmptyTypeRow";
import { withPlanEligibility, isClientPlanEligible, typeIneligibleReason } from "@/lib/planEligibility";
import { ModifyTypeModal } from "@/components/benefits/v2/ModifyTypeModal";
import { useTypeSections } from "@/components/benefits/v2/useTypeSections";
import { normalizePlanType } from "@shared/schemas/planTypes";
import { redactSsnDeep } from "@shared/utils/ssnRedaction";



// Documents Tab Component
function DocumentsTabContent({ employeeId, clientId, employeeName }: { employeeId: string; clientId?: number; employeeName: string }) {
  const { documents, loading, error, refreshDocuments } = useDocuments({
    ownerType: "employee",
    employeeId: parseInt(employeeId, 10),
    clientId,
  });
  const { toast } = useToast();

  return (
    <TabsContent value="documents">
      <Card className="border-l-4 border-l-green-500">
        <CardHeader className="bg-green-50 dark:bg-green-950/20">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Documents
            </CardTitle>
            <DocumentUpload
              ownerType="employee"
              employeeId={parseInt(employeeId, 10)}
              clientId={clientId}
              onUploadSuccess={async () => {
                await refreshDocuments();
                toast({
                  title: "Document uploaded",
                  description: `Document added for ${employeeName}`,
                  variant: "success",
                });
              }}
              trigger={
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Upload Document
                </Button>
              }
            />
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Loading documents...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Error loading documents</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <DocumentList
              documents={documents}
              onDocumentUpdate={refreshDocuments}
              showActions={true}
              showStatus={false}
              emptyStateAction={
                <DocumentUpload
                  ownerType="employee"
                  employeeId={parseInt(employeeId, 10)}
                  clientId={clientId}
                  onUploadSuccess={refreshDocuments}
                  trigger={
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Upload First Document
                    </Button>
                  }
                />
              }
            />
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}

// Breadcrumb employee name -> searchable dropdown to jump between employees.
function EmployeeNameSwitcher({
  employees,
  currentId,
  currentName,
  onSelect,
}: {
  employees: Employee[];
  currentId: number;
  currentName: string;
  onSelect: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          className="flex items-center gap-1 font-medium text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300"
        >
          {currentName}
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search employees..." className="h-9" />
          <CommandList>
            <CommandEmpty>No employees found.</CommandEmpty>
            <CommandGroup>
              {employees.map((emp) => (
                <CommandItem
                  key={emp.id}
                  value={`${emp.firstName} ${emp.lastName} ${emp.id}`}
                  onSelect={() => {
                    onSelect(emp.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-3.5 w-3.5 ${emp.id === currentId ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="truncate">
                    {emp.firstName} {emp.lastName}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const EMPLOYEE_TABS = [
  "profile",
  "employment",
  "benefits",
  "dependents",
  "aca",
  "history",
  "documents",
] as const;

export default function EmployeeDetail() {
  const { employeeId } = useParams();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClientInstance = useQueryClient();
  const { canAny } = usePermissions();
  const [isEditing, setIsEditing] = useState(false);
  // URL-based tab persistence using nuqs
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringLiteral(EMPLOYEE_TABS).withDefault("profile")
  );
  const [showSSN, setShowSSN] = useState(false);
  // Full SSN fetched on demand (API rows carry ***-**-1234); undefined = not revealed.
  const [revealedSsn, setRevealedSsn] = useState<string | null | undefined>(undefined);
  const [showAddEmergencyContact, setShowAddEmergencyContact] = useState(false);
  const [editingEmergencyContact, setEditingEmergencyContact] = useState<EmergencyContact | null>(null);
  const [showAddDependent, setShowAddDependent] = useState(false);
  const [showAddContactHistory, setShowAddContactHistory] = useState(false);
  const [showConfigureBenefits, setShowConfigureBenefits] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState<any>(null);
  const [showEditEnrollment, setShowEditEnrollment] = useState(false);
  // Per-type Modify modal context (replaces ModifyActiveEnrollmentModal + ModifyWaivedModal).
  const [modifyContext, setModifyContext] = useState<{
    type: string;
    current?: any | null;
    waived?: any | null;
  } | null>(null);
  // Scoped Configure-Benefits wizard. When set, BenefitsConfigurationForm renders only these types.
  const [configureScope, setConfigureScope] = useState<string[] | null>(null);
  const [editCoverageTier, setEditCoverageTier] = useState<string>("");
  const [editEffectiveDate, setEditEffectiveDate] = useState<string>("");
  const [editWaived, setEditWaived] = useState<boolean>(false);
  const [editEventType, setEditEventType] = useState<string>("");
  const [editEventDescription, setEditEventDescription] = useState<string>("");
  const [editSelectedDependentIds, setEditSelectedDependentIds] = useState<
    number[]
  >([]);
  const [editBeneficiaryName, setEditBeneficiaryName] = useState<string>("");
  const [editBeneficiaryRelationship, setEditBeneficiaryRelationship] =
    useState<string>("");
  const [addDepSelection, setAddDepSelection] = useState<
    Record<number, boolean>
  >({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  // DEV-175: send-login flow now has two modes.
  const [loginMode, setLoginMode] = useState<"reset_link" | "temp_password">("reset_link");
  const [loginEmailUser, setLoginEmailUser] = useState(true);
  const [loginResult, setLoginResult] = useState<{
    mode: "reset_link" | "temp_password";
    tempPassword?: string;
    setupUrl?: string;
    emailSent: boolean;
    email: string;
  } | null>(null);
  const [loginCopied, setLoginCopied] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [pendingEmploymentData, setPendingEmploymentData] = useState<any>(null);
  const profileFormRef = useRef<HTMLFormElement | null>(null);
  const employmentFormRef = useRef<HTMLFormElement | null>(null);
  
  const {
    verifyChanges,
    VerificationModal: EmploymentVerificationModal,
    isVerifying,
  } = useEmploymentChangeVerification();

  // Termination state
  const terminateMutation = useMutation({
    mutationFn: async (payload: { terminationDate?: string; terminationReason?: string; notes?: string }) => {
      if (!employeeId) throw new Error("Missing employeeId");
      const r = await fetch(`/api/employees/${employeeId}/terminate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      });
      if (!r.ok) {
        const msg = (await r.json().catch(() => ({})))?.message || "Failed";
        throw new Error(msg);
      }
      return r.json();
    },
    onSuccess: () => {
      setShowTerminateModal(false);
      toast({
        title: "Employee terminated",
        description: "All enrollments ended.",
      });
      // Invalidate employee, enrollments, benefits, dependents
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}`],
      });
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/enrollments", Number(employeeId)],
      });
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/employees", Number(employeeId), "benefits"],
      });
      
      // Invalidate client-level queries used by EnrollmentsTab
      if (employee?.clientId) {
        const clientId = String(employee.clientId);
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/enrollments", clientId],
        });
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/dependents", employee.clientId],
        });
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/clients", clientId, "employees"],
        });
      }
    },
    onError: (e: any) => {
      toast({
        title: "Termination failed",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleTerminate = () => {
    if (!employee) return;
    setShowTerminateModal(true);
  };

  const handleTerminationConfirm = (data: { terminationDate: string; terminationReason: string; notes?: string }) => {
    terminateMutation.mutate(data);
  };

  // Re-hire mutation for terminated employees
  const rehireMutation = useMutation({
    mutationFn: async (payload: { rehireDate?: string; notes?: string }) => {
      if (!employeeId) throw new Error("Missing employeeId");
      const r = await fetch(`/api/employees/${employeeId}/rehire`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      });
      if (!r.ok) {
        const msg = (await r.json().catch(() => ({})))?.message || "Failed";
        throw new Error(msg);
      }
      return r.json();
    },
    onSuccess: () => {
      toast({
        title: "Employee re-hired",
        description: "Employee status updated to active.",
      });
      // Invalidate employee queries
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}`],
      });
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/employees"],
      });
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/employees", Number(employeeId), "benefits"],
      });
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/history`],
      });
      if (employee?.clientId) {
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/clients", String(employee.clientId), "employees"],
        });
      }
    },
    onError: (e: any) => {
      toast({
        title: "Re-hire failed",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleRehire = () => {
    if (!employee) return;
    const today = new Date().toISOString().split("T")[0];
    if (window.confirm(`Re-hire ${employee.firstName} ${employee.lastName}? This will set their status to active with today's date as the new hire date.`)) {
      rehireMutation.mutate({ rehireDate: today });
    }
  };

  const { data: employee, isLoading: isLoadingEmployee } = useQuery<Employee>({
    queryKey: [`/api/employees/${employeeId}`],
    enabled: !!employeeId,
  });

  // Fetch all employees for navigation
  // Priority: clientId > prospectId > agencyId (navigate within client/prospect context first)
  const { data: allEmployees = [] } = useQuery<Employee[]>({
    queryKey: employee?.clientId
      ? [`/api/clients/${employee.clientId}/employees`]
      : employee?.prospectId
      ? [`/api/prospects/${employee.prospectId}/employees`]
      : employee?.agencyId
      ? ["/api/agency/employees"]
      : ["employees-navigation"],
    enabled: !!employee && !!(employee.agencyId || employee.clientId || employee.prospectId),
  });

  // Calculate previous and next employee IDs
  const sortedEmployees = sortEmployeesByFullName(allEmployees);
  const currentIndex = sortedEmployees.findIndex((emp: Employee) => emp.id === parseInt(employeeId || "0"));
  const previousEmployee = currentIndex > 0 ? sortedEmployees[currentIndex - 1] : null;
  const nextEmployee = currentIndex < sortedEmployees.length - 1 ? sortedEmployees[currentIndex + 1] : null;

  // Navigate to another employee, keeping the same owner context + active tab.
  const navigateToEmployee = (targetId: number) => {
    const base = employee?.clientId
      ? `/clients/${employee.clientId}/employees/${targetId}`
      : employee?.prospectId
      ? `/prospects/${employee.prospectId}/employees/${targetId}`
      : `/agency/employees/${targetId}`;
    setLocation(`${base}?tab=${activeTab}`);
  };

  // Check if employee can be deleted (no hire date, no enrollments)
  const { data: canDeleteData } = useQuery({
    queryKey: [`/api/employees/${employeeId}/can-delete`],
    enabled: !!employeeId,
  });

  // Determine organization owner context (client, prospect, or agency)
  const { data: profile } = useQuery({
    queryKey: ["/api/user/profile"],
    enabled: !employee?.clientId && !employee?.prospectId,
    queryFn: async () => {
      const r = await fetch("/api/user/profile");
      if (!r.ok) throw new Error("Failed to load profile");
      return r.json();
    },
  });

  const orgOwnerType = employee?.clientId
    ? "client"
    : employee?.prospectId
    ? "prospect"
    : "agency";
  const orgOwnerId =
    orgOwnerType === "client"
      ? employee?.clientId
      : orgOwnerType === "prospect"
      ? employee?.prospectId
      : profile?.id;

  // Load organization reference lists for the employee's owner context
  const { data: orgDepartments = [] } = useQuery({
    queryKey: ["/api/org/departments", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/departments?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });
  const { data: orgJobTitles = [] } = useQuery({
    queryKey: ["/api/org/job-titles", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/job-titles?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });
  const { data: orgLocations = [] } = useQuery({
    queryKey: ["/api/org/locations", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/locations?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });
  const { data: orgEmployeeClasses = [] } = useQuery({
    queryKey: ["/api/org/employee-classes", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/employee-classes?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });
  const { data: orgDivisions = [] } = useQuery({
    queryKey: ["/api/org/divisions", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/divisions?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  // Filter potential managers (exclude current employee)
  const potentialManagers = useMemo(() => {
    return allEmployees.filter(
      (emp: Employee) => String(emp.id) !== String(employeeId)
    );
  }, [allEmployees, employeeId]);

  // Owner payload for upserts from employment section
  const ownerPayload: any =
    orgOwnerType === "client"
      ? { ownerType: "client", clientId: orgOwnerId }
      : orgOwnerType === "prospect"
      ? { ownerType: "prospect", prospectId: orgOwnerId }
      : { ownerType: "agency", agencyId: String(orgOwnerId || "") };

  // Quick-add dialogs for uniform UX
  const [isAddJobTitleOpen, setIsAddJobTitleOpen] = useState(false);
  const [isAddDepartmentOpen, setIsAddDepartmentOpen] = useState(false);
  const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
  const [isAddClassOpen, setIsAddClassOpen] = useState(false);
  const [isAddDivisionOpen, setIsAddDivisionOpen] = useState(false);

  // Quick-add state for upserts from employment section
  const [newJobTitle, setNewJobTitle] = useState("");
  const [newDepartment, setNewDepartment] = useState("");
  const [newClass, setNewClass] = useState("");
  const [newLocOffice, setNewLocOffice] = useState("");
  const [newLocCity, setNewLocCity] = useState("");
  const [newLocState, setNewLocState] = useState("");
  const [newDivision, setNewDivision] = useState("");

  // Upsert mutations for organization refs
  const addJobTitle = useMutation({
    mutationFn: async (name: string) => {
      const r = await fetch(`/api/org/job-titles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...ownerPayload, name }),
      });
      if (!r.ok) throw new Error("Failed to add job title");
      return r.json();
    },
    onSuccess: (row: any) => {
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/org/job-titles", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("jobTitleId", row.id, { shouldDirty: true });
      setNewJobTitle("");
      setIsAddJobTitleOpen(false);
    },
  });

  const addDepartment = useMutation({
    mutationFn: async (name: string) => {
      const r = await fetch(`/api/org/departments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...ownerPayload, name }),
      });
      if (!r.ok) throw new Error("Failed to add department");
      return r.json();
    },
    onSuccess: (row: any) => {
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/org/departments", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("departmentId", row.id, { shouldDirty: true });
      setNewDepartment("");
      setIsAddDepartmentOpen(false);
    },
  });

  const addLocation = useMutation({
    mutationFn: async (payload: {
      officeName?: string;
      city?: string;
      state?: string;
    }) => {
      const r = await fetch(`/api/org/locations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...ownerPayload, ...payload }),
      });
      if (!r.ok) throw new Error("Failed to add location");
      return r.json();
    },
    onSuccess: (row: any) => {
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/org/locations", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("locationId", row.id, { shouldDirty: true });
      setNewLocOffice("");
      setNewLocCity("");
      setNewLocState("");
      setIsAddLocationOpen(false);
    },
  });

  const addEmployeeClass = useMutation({
    mutationFn: async (name: string) => {
      const r = await fetch(`/api/org/employee-classes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...ownerPayload, name }),
      });
      if (!r.ok) throw new Error("Failed to add employee class");
      return r.json();
    },
    onSuccess: (row: any) => {
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/org/employee-classes", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("employeeClassId", row.id, { shouldDirty: true });
      setNewClass("");
      setIsAddClassOpen(false);
    },
  });

  const addDivision = useMutation({
    mutationFn: async (name: string) => {
      const r = await fetch(`/api/org/divisions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...ownerPayload, name }),
      });
      if (!r.ok) throw new Error("Failed to add division");
      return r.json();
    },
    onSuccess: (row: any) => {
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/org/divisions", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("divisionId", row.id, { shouldDirty: true });
      setNewDivision("");
      setIsAddDivisionOpen(false);
    },
  });

  const { data: client } = useQuery<Client>({
    queryKey: [`/api/clients/${employee?.clientId}`],
    enabled: !!employee?.clientId,
  });

  // Get clientId for plan customizations
  const clientId = employee?.clientId;

  const { data: emergencyContacts = [] } = useQuery<EmergencyContact[]>({
    queryKey: [`/api/employees/${employeeId}/emergency-contacts`],
    queryFn: () =>
      fetch(`/api/employees/${employeeId}/emergency-contacts`).then((res) =>
        res.json()
      ),
    enabled: !!employeeId,
  });

  const { data: dependents = [] } = useQuery<Dependent[]>({
    queryKey: [`/api/employees/${employeeId}/dependents`],
    queryFn: async () => {
      if (!employeeId) return [];
      const res = await fetch(`/api/employees/${employeeId}/dependents`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!employeeId,
  });

  const isClientScoped = !!employee?.clientId;
  const { data: contactHistory = [] } = useQuery<ContactInteraction[]>({
    // Keyed per employee — a client/prospect-level key is shared by every
    // employee of that company, so navigating between employees would show
    // the previous employee's cached interactions.
    queryKey: ["/api/employees", employeeId, "contact-interactions"],
    queryFn: async () => {
      // Prefer employee-specific history; fall back to client/prospect if present
      if (employeeId) {
        const r = await fetch(
          `/api/employees/${employeeId}/contact-interactions`
        );
        if (r.ok) return r.json();
      }
      const targetId = employee?.clientId || employee?.prospectId;
      if (!targetId) return [] as ContactInteraction[];
      const res = await fetch(
        employee?.clientId
          ? `/api/clients/${targetId}/contact-interactions`
          : `/api/prospects/${targetId}/contact-interactions`
      );
      if (!res.ok) return [] as ContactInteraction[];
      return res.json();
    },
    enabled: !!employeeId || !!employee?.clientId || !!employee?.prospectId,
  });

  // Employee History Logs (changes to employee records)
  const { data: historyLogs = [] } = useQuery({
    queryKey: [`/api/employees/${employeeId}/history`],
    queryFn: async () => {
      if (!employeeId) return [] as any[];
      const res = await fetch(`/api/employees/${employeeId}/history`);
      if (!res.ok) return [] as any[];
      return res.json();
    },
    enabled: !!employeeId,
  });

  // Employee Benefits (enrollments with plan details)
  const { data: employeeBenefits = [], isLoading: isLoadingEmployeeBenefits } = useQuery({
    queryKey: ["/api/employees", employeeId, "benefits"],
    queryFn: async () => {
      if (!employeeId) return [] as any[];
      const res = await fetchWithAuth(`/api/employees/${parseInt(employeeId)}/benefits`);
      if (!res.ok) throw new Error("Failed to fetch employee benefits");
      return res.json();
    },
    enabled: !!employeeId,
  });

  // Deduplicate base enrollments by planId (keep the most recently updated per plan)
  const baseEnrollments = useMemo(() => {
    const bases = (employeeBenefits as any[]).filter((b: any) => b.dependentId == null);
    const byPlan = new Map<number, any>();
    for (const b of bases) {
      const pid = Number(b?.planId);
      if (!Number.isFinite(pid)) continue;
      const existing = byPlan.get(pid);
      if (!existing) {
        byPlan.set(pid, b);
      } else {
        // Keep the more recently updated one
        const bTime = new Date(b?.updatedAt || b?.lastModified || b?.effectiveDate || 0).getTime() || 0;
        const eTime = new Date(existing?.updatedAt || existing?.lastModified || existing?.effectiveDate || 0).getTime() || 0;
        if (bTime > eTime) byPlan.set(pid, b);
      }
    }
    return Array.from(byPlan.values());
  }, [employeeBenefits]);

  const activeBenefits = filterActiveEnrollments(baseEnrollments as any);

  const pendingBenefits = useMemo(() => {
    const activeIds = new Set(activeBenefits.map((e: any) => e.id));
    return (baseEnrollments as any[]).filter((e: any) => {
      if (e.waivedCoverage) return false;
      if (activeIds.has(e.id)) return false;
      const status = String(e.status || "").toLowerCase();
      if (status === "terminated") return false;
      return status === "pending" || isPendingEnrollment(e);
    });
  }, [baseEnrollments, activeBenefits]);

  // Only currently-effective enrollments count toward deductions
  const effectiveBenefits = useMemo(
    () => activeBenefits.filter(isCurrentlyEffectiveEnrollment),
    [activeBenefits]
  );

  // Prepare active enrollments data for termination modal with termination timing
  const activeEnrollmentsForTermination = useMemo(() => {
    if (!activeBenefits) return [];
    return activeBenefits.map((e: any) => ({
      id: e.id,
      planName: e.planName || e.plan?.name || "Unknown Plan",
      planType: e.planType || e.plan?.planType || "health",
      coverageTier: e.coverageTier || e.tier || "Employee Only",
      terminationTiming: e.plan?.terminationTiming || "end_of_month",
      employeePremium: e.employeePremium || e.premium?.employeePremium,
      totalPremium: e.totalPremium || e.premium?.totalPremium,
    }));
  }, [activeBenefits]);

  const waivedBenefits = filterWaivedEnrollments(baseEnrollments as any);

  // DEV-382: a plan the server couldn't price (no rates for the group's rating
  // area) is a null line, never a $0 in the total.
  const deductionItems = (effectiveBenefits as any[]).map((b: any) => ({
    label: b.plan?.name || b.plan?.type || "Benefit",
    monthlyAmount: enrollmentEmployeeMonthly(b),
  }));
  const { totalMonthly, unpricedPlans } = totalPricedMonthly(deductionItems);

  const formatCurrency = (n: number | string | null | undefined) => {
    const v = typeof n === "number" ? n : parseFloat(String(n ?? 0));
    if (!Number.isFinite(v)) return "$0.00";
    return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
  };

  function normalizeCoverageTierLabel(coverageTier: string) {
    return String(coverageTier || "").replace(/_/g, " ");
  }


  // Note: updateEnrollmentMutation is defined later for PATCH updates; reuse that

  // DEV-262: removed fetchPlanRatesAndContribsLocal here — a caller-less copy
  // of fetchPlanPremiumData's job (and it missed buy-up base plans). Use
  // fetchPlanPremiumData from @/hooks/usePlanPremiumData if this page ever
  // needs per-plan rates + contributions again.

  const { data: enrollments = [] } = useQuery<Enrollment[]>({
    queryKey: ["/api/enrollments"],
    enabled: !!employeeId,
    select: (data) =>
      data.filter(
        (enrollment: any) =>
          enrollment.employeeId === parseInt(employeeId || "0")
      ),
  });

  // Debug: Log deletion eligibility
  console.log('Employee deletion eligibility:', {
    employeeId,
    canDeleteData,
    hireDate: employee?.hireDate,
  });

  const { data: allPlans = [] } = useQuery<Plan[]>({
    queryKey: ["/api/plans"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/plans");
        if (!res.ok) return [] as any[];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      } catch {
        return [] as any[];
      }
    },
  });

  // Fetch client-specific plan associations
  const { data: clientPlans = [], isLoading: isLoadingClientPlans } = useQuery({
    queryKey: ["/api/client-plans", employee?.clientId],
    queryFn: async () => {
      try {
        const res = await fetch(
          `/api/client-plans?clientId=${employee?.clientId}`
        );
        if (!res.ok) return [] as any[];
        const data = await res.json();
        console.log("Fetched client plans for clientId", employee?.clientId, ":", data);
        // Map plans data to ensure consistency with expected format
        return Array.isArray(data) ? data : [];
      } catch {
        return [] as any[];
      }
    },
    enabled: !!employee?.clientId,
  });

  // Per-plan eligibility (same decision the enrollment API enforces). The key
  // includes the fields it depends on so an Employment tab save refetches it.
  const { data: planEligibility } = useQuery<any>({
    queryKey: [
      "employee-eligibility",
      Number(employeeId),
      employee?.employmentType,
      employee?.scheduledHours,
      employee?.hireDate,
      employee?.state,
      (employee as any)?.locationId,
      (employee as any)?.employeeClassId,
    ],
    queryFn: async () => {
      const res = await fetch(`/api/employees/${employeeId}/eligibility`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!employeeId,
  });
  const clientPlansWithEligibility = useMemo(
    () => withPlanEligibility(clientPlans as any[], planEligibility?.plans, Boolean(employee?.hireDate)),
    [clientPlans, planEligibility, employee?.hireDate],
  );

  // Open enrollment-request task (a QLE/off-cycle election pending broker review).
  const { data: pendingEnrollmentTask } = useQuery<any>({
    queryKey: ["/api/tasks", "enrollment-request", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const url = new URL("/api/tasks", window.location.origin);
      url.searchParams.set("status", "open");
      url.searchParams.set("employeeId", String(employeeId));
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) return null;
      const json = await res.json();
      const list: any[] = json?.tasks || json?.tickets || [];
      return list.find(isEnrollmentRequestTask) || null;
    },
  });

  // DEV-262: removed two dead queries here — a whole-table /api/plan-rates
  // download and /api/plan-age-rates (legacy age-multiplier system), neither
  // read anywhere in this file. Premium math on this page goes through
  // server-computed values; the contributions below feed Cost Summary +
  // waived-row needs-config only.

  // Fetch plan contributions (used by Cost Summary section)
  const { data: planContributions = [], isFetched: planContributionsFetched } = useQuery({
    queryKey: ["/api/plan-contributions"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/plan-contributions");
        if (!res.ok) return [] as any[];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      } catch {
        return [] as any[];
      }
    },
  });

  // Type-sectioned view derivation (page IA — broker + employer share this shape).
  const typeSections = useTypeSections({
    activeBenefits: activeBenefits as any[],
    pendingBenefits: pendingBenefits as any[],
    waivedBenefits: waivedBenefits as any[],
    clientPlans: clientPlans as any[],
  });

  const offeredTypeIds = useMemo(() => {
    const s = new Set<string>();
    for (const cp of (clientPlans as any[]) || []) {
      const t = normalizePlanType(cp?.plan?.type);
      if (t) s.add(t === "health" ? "medical" : t);
    }
    return s;
  }, [clientPlans]);

  const openConfigureBenefits = (scope: string[] | null) => {
    setConfigureScope(scope);
    setShowConfigureBenefits(true);
  };

  const openModifyForType = (planType: string) => {
    const t = normalizePlanType(planType);
    const current = (baseEnrollments as any[]).find((b: any) => {
      if (b.waivedCoverage) return false;
      if (String(b.status || "").toLowerCase() === "terminated") return false;
      return normalizePlanType(b?.plan?.type) === t;
    });
    const waived = (waivedBenefits as any[]).find(
      (b: any) => normalizePlanType(b?.plan?.type) === t,
    );
    setModifyContext({ type: t, current, waived });
  };

  const plansForType = (planType: string) => {
    const t = normalizePlanType(planType);
    return clientPlansWithEligibility.filter(
      (cp: any) => normalizePlanType(cp?.plan?.type) === t,
    );
  };

  const updateEmployeeMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`/api/employees/${employeeId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to update employee");
      return response.json();
    },
    onMutate: async (newData) => {
      // Cancel any outgoing refetches
      await queryClientInstance.cancelQueries({
        queryKey: [`/api/employees/${employeeId}`]
      });

      // Snapshot the previous value
      const previousEmployee = queryClientInstance.getQueryData([
        `/api/employees/${employeeId}`,
      ]);

      // Optimistically update to the new value
      queryClientInstance.setQueryData(
        [`/api/employees/${employeeId}`],
        (old: any) => ({
          ...old,
          ...newData,
        })
      );

      // Exit edit mode immediately for better UX
      setIsEditing(false);

      // Return context with the previous value
      return { previousEmployee };
    },
    onSuccess: async () => {
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}`],
      });
      
      // Invalidate the relevant employee list
      if (employee?.clientId) {
        const clientId = String(employee.clientId);
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/clients", clientId, "employees"],
        });
        // Invalidate client-level enrollments and dependents queries used by EnrollmentsTab
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/enrollments", clientId],
        });
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/dependents", employee.clientId],
        });
      } else if (employee?.prospectId) {
        queryClientInstance.invalidateQueries({
          queryKey: [`/api/prospects/${employee.prospectId}/employees`],
        });
      } else if (employee?.agencyId) {
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/agency/employees"],
        });
      }
      
      toast({ title: "Employee updated successfully" , variant: "success"});
      
      try {
        const profileChanges = (window as any).__profileChanges || {};
        const employmentChanges = (window as any).__employmentChanges || {};
        const allChanges = { ...profileChanges, ...employmentChanges };
        const changeCount = Object.keys(allChanges).length;
        const description = changeCount > 0 
          ? `Updated employee information`
          : "Employee details updated";
        
        const response = await fetch(`/api/employees/${employeeId}/history`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "employee_updated",
            description,
            recordType: "employee",
            recordId: Number(employeeId),
            changes: allChanges,
          }),
        });
        
        if (response.ok) {
          queryClientInstance.invalidateQueries({ 
            queryKey: ["/api/employees", employeeId, "contact-interactions"] 
          });
        }
        
        // Clean up temp storage
        delete (window as any).__profileChanges;
        delete (window as any).__employmentChanges;
      } catch (err) {
        console.error("Failed to create employee history:", err);
      }
    },
    onError: (error, newData, context) => {
      // Rollback to the previous value on error
      if (context?.previousEmployee) {
        queryClientInstance.setQueryData(
          [`/api/employees/${employeeId}`],
          context.previousEmployee
        );
      }
      // Re-open edit mode on error
      setIsEditing(true);
      toast({ title: "Failed to update employee", variant: "destructive" });
    },
  });

  const deleteEmployeeMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/employees/${employeeId}`, {
        method: "DELETE"
      });
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || "Failed to delete employee");
      }
      return response.json();
    },
    onSuccess: () => {
      const clientId = employee?.clientId;
      // Invalidate all relevant queries to ensure UI updates
      if (clientId) {
        queryClientInstance.invalidateQueries({ 
          queryKey: ["/api/clients", String(clientId), "employees"] 
        });
      }
      queryClientInstance.invalidateQueries({ queryKey: ["/api/employees"] });
      
      toast({ title: "Employee deleted successfully" , variant: "success"});
      
      // Navigate back to client's employee list tab
      if (clientId) {
        setLocation(`/clients/${clientId}?tab=employees`);
      }
    },
    onError: (error: any) => {
      const message = error?.message || "Failed to delete employee";
      toast({ title: message, variant: "destructive" });
    },
  });

  const sendLoginMutation = useMutation({
    mutationFn: async (options: {
      mode: "reset_link" | "temp_password";
      sendEmail: boolean;
    }) => {
      const response = await fetch(`/api/employees/${employeeId}/send-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(options),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send login credentials");
      }
      return response.json();
    },
    onSuccess: (data: any) => {
      setLoginCopied(false);
      if (data.mode === "reset_link" && data.emailSent) {
        toast({
          title: data.isNewUser ? "Setup link sent" : "Reset link sent",
          description: `Emailed to ${data.email}. Link expires in 7 days.`,
        });
        setShowLoginModal(false);
      } else {
        setLoginResult({
          mode: data.mode,
          tempPassword: data.tempPassword,
          setupUrl: data.setupUrl,
          emailSent: !!data.emailSent,
          email: data.email,
        });
      }
    },
    onError: (error: any) => {
      const message = error?.message || "Failed to send login credentials";
      toast({ title: message, variant: "destructive" });
    },
  });

  const createEmergencyContactMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(
        `/api/employees/${employeeId}/emergency-contacts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );
      if (!response.ok) throw new Error("Failed to create emergency contact");
      return response.json();
    },
    onSuccess: async (created: any) => {
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/emergency-contacts`],
      });
      toast({ title: "Emergency contact added successfully" , variant: "success"});
      setShowAddEmergencyContact(false);
      setEditingEmergencyContact(null);
      
      try {
        const contactName = created?.name || (window as any).__emergencyContactAddName || "New Contact";
        
        const response = await fetch(`/api/employees/${employeeId}/history`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "emergency_contact_added",
            description: `Added emergency contact: ${contactName}`,
            recordType: "emergency_contact",
            recordId: created?.id ?? null,
          }),
        });
        
        if (response.ok) {
          queryClientInstance.invalidateQueries({ 
            queryKey: ["/api/employees", employeeId, "contact-interactions"] 
          });
        }
        delete (window as any).__emergencyContactAddName;
      } catch (err) {
        console.error("Failed to create emergency contact history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Failed to add emergency contact",
        variant: "destructive",
      });
    },
  });

  const updateEmergencyContactMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await fetch(`/api/emergency-contacts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to update emergency contact");
      return response.json();
    },
    onSuccess: async (updated: any) => {
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/emergency-contacts`],
      });
      toast({ title: "Emergency contact updated successfully", variant: "success" });
      setShowAddEmergencyContact(false);
      setEditingEmergencyContact(null);
      
      try {
        const changes = (window as any).__emergencyContactChanges || {};
        const contactName = (window as any).__emergencyContactUpdateName || updated?.name || "Emergency Contact";
        const description = `Updated emergency contact: ${contactName}`;
        
        const response = await fetch(`/api/employees/${employeeId}/history`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "emergency_contact_updated",
            description,
            recordType: "emergency_contact",
            recordId: updated?.id ?? null,
            changes,
          }),
        });
        
        if (response.ok) {
          queryClientInstance.invalidateQueries({ 
            queryKey: ["/api/employees", employeeId, "contact-interactions"] 
          });
        }
        delete (window as any).__emergencyContactChanges;
        delete (window as any).__emergencyContactUpdateName;
      } catch (err) {
        console.error("Failed to create emergency contact update history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Failed to update emergency contact",
        variant: "destructive",
      });
    },
  });

  const deleteEmergencyContactMutation = useMutation({
    mutationFn: async (contactId: number) => {
      const response = await fetch(`/api/emergency-contacts/${contactId}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete emergency contact");
      if (response.status === 204) return null;
      try {
        return await response.json();
      } catch {
        return null;
      }
    },
    onSuccess: async (_data: any, contactId: number) => {
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/emergency-contacts`],
      });
      toast({ title: "Emergency contact deleted successfully" , variant: "success"});
      
      try {
        const contactName = (window as any).__emergencyContactDeleteName || "Emergency Contact";
        const response = await fetch(`/api/employees/${employeeId}/history`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "emergency_contact_deleted",
            description: `Deleted emergency contact: ${contactName}`,
            recordType: "emergency_contact",
            recordId: contactId ?? null,
          }),
        });
        
        if (response.ok) {
          queryClientInstance.invalidateQueries({ 
            queryKey: ["/api/employees", employeeId, "contact-interactions"] 
          });
        }
        delete (window as any).__emergencyContactDeleteName;
      } catch (err) {
        console.error("Failed to create emergency contact deletion history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Failed to delete emergency contact",
        variant: "destructive",
      });
    },
  });

  const createDependentMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch("/api/dependents", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          employeeId: parseInt(employeeId || "0"),
        }),
      });
      if (!response.ok) throw new Error("Failed to create dependent");
      return response.json();
    },
    onSuccess: async (created: any) => {
      queryClientInstance.invalidateQueries({ queryKey: ["/api/dependents"] });
      // Invalidate employee-specific dependents query
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      
      // Invalidate client-level queries used by EnrollmentsTab
      if (employee?.clientId) {
        const clientId = String(employee.clientId);
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/dependents", employee.clientId],
        });
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/enrollments", clientId],
        });
      }
      
      toast({ title: "Dependent added successfully" , variant: "success"});
      setShowAddDependent(false);
      
      try {
        const dependentName = `${created?.firstName || ""} ${created?.lastName || ""}`.trim() || "New Dependent";
        
        const response = await fetch(`/api/employees/${employeeId}/history`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "dependent_added",
            description: `Added dependent: ${dependentName}`,
            recordType: "dependent",
            recordId: created?.id ?? null,
          }),
        });
        
        if (response.ok) {
          queryClientInstance.invalidateQueries({ 
            queryKey: ["/api/employees", employeeId, "contact-interactions"] 
          });
        }
      } catch (err) {
        console.error("Failed to create dependent history:", err);
      }
    },
    onError: () => {
      toast({ title: "Failed to add dependent", variant: "destructive" });
    },
  });

  const deleteDependentMutation = useMutation({
    mutationFn: async (dependentId: number) => {
      const response = await fetch(`/api/dependents/${dependentId}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete dependent");
      return response.json();
    },
    onSuccess: async (_data: any, dependentId: number) => {
      queryClientInstance.invalidateQueries({ queryKey: ["/api/dependents"] });
      // Invalidate employee-specific dependents query
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      
      // Invalidate client-level queries used by EnrollmentsTab
      if (employee?.clientId) {
        const clientId = String(employee.clientId);
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/dependents", employee.clientId],
        });
        queryClientInstance.invalidateQueries({
          queryKey: ["/api/enrollments", clientId],
        });
      }
      
      toast({ title: "Dependent deleted successfully" , variant: "success"});
      
      try {
        // Server already creates history entry for dependent deletion
        queryClientInstance.invalidateQueries({ 
          queryKey: ["/api/employees", employeeId, "contact-interactions"] 
        });
      } catch (err) {
        console.error("Failed to invalidate history:", err);
      }
    },
    onError: () => {
      toast({ title: "Failed to delete dependent", variant: "destructive" });
    },
  });

  const addEnrollmentMutation = useMutation({
    mutationFn: async (enrollmentData: any) => {
      const response = await fetch("/api/enrollments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(enrollmentData),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Failed to add enrollment");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClientInstance.invalidateQueries({ queryKey: ["/api/enrollments"] });
      queryClientInstance.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      enrollmentForm.reset();
      toast({ title: "Enrollment added successfully" , variant: "success"});
    },
    onError: (error: any) => {
      toast({ title: "Failed to add enrollment", description: error.message, variant: "destructive" });
    },
  });

  const updateEnrollmentMutation = useMutation({
    mutationFn: async (data: any) => {
      const { id, ...updateData } = data;
      const response = await fetch(`/api/enrollments/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });
      if (!response.ok) throw new Error("Failed to update enrollment");
      return response.json();
    },
    onSuccess: () => {
      queryClientInstance.invalidateQueries({ queryKey: ["/api/enrollments"] });
      setShowEditEnrollment(false);
      setEditingEnrollment(null);
      toast({ title: "Enrollment updated successfully" , variant: "success"});
    },
  });

  type ProfileFormValues = Pick<
    InsertEmployee,
    | "firstName"
    | "lastName"
    | "personalEmail"
    | "phone"
    | "dateOfBirth"
    | "sex"
    | "maritalStatus"
    | "ssn"
    | "tobaccoUser"
    | "disabled"
    | "address1"
    | "address2"
    | "city"
    | "state"
    | "zipCode"
    | "county"
    | "country"
    | "status"
  >;

  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(
      insertEmployeeSchema.pick({
        firstName: true,
        lastName: true,
        personalEmail: true,
        phone: true,
        dateOfBirth: true,
        sex: true,
        maritalStatus: true,
        ssn: true,
        tobaccoUser: true,
        disabled: true,
        address1: true,
        address2: true,
        city: true,
        state: true,
        zipCode: true,
        county: true,
        country: true,
        status: true,
      })
    ),
    defaultValues: {
      firstName: employee?.firstName || "",
      lastName: employee?.lastName || "",
      personalEmail: (employee as any)?.personalEmail || "",
      phone: employee?.phone || "",
      dateOfBirth: employee?.dateOfBirth
        ? (parseDateLocal(employee.dateOfBirth) ?? new Date(employee.dateOfBirth)).toISOString().split("T")[0]
        : "",
      sex: employee?.sex || "",
      maritalStatus: employee?.maritalStatus || "",
      ssn: employee?.ssn || "",
      tobaccoUser: employee?.tobaccoUser || "",
      disabled: employee?.disabled || "",
      address1: employee?.address1 || "",
      address2: employee?.address2 || "",
      city: employee?.city || "",
      state: employee?.state || "",
      zipCode: employee?.zipCode || "",
      county: employee?.county || "",
      country: employee?.country || "",
      status: normalizeStatus(employee?.status) || "active",
    },
  });

  type EmploymentFormValues = Pick<
    InsertEmployee,
    | "position"
    | "department"
    | "hireDate"
    | "employmentType"
    | "employeeId"
    | "scheduledHours"
    | "compensationType"
    | "compensationAmount"
    | "payCycle"
    | "status"
    | "email"
    | "workPhone"
    | "licenseNumber"
  > & {
    compensationEffectiveDate?: string | null;
    departmentId?: number | null;
    jobTitleId?: number | null;
    locationId?: number | null;
    employeeClassId?: number | null;
    divisionId?: number | null;
    managerEmployeeId?: number | null;
  };

  const employmentForm = useForm<EmploymentFormValues>({
    // .passthrough() keeps every other field as unvalidated as before -
    // only employmentType gets the shared schema's required rule.
    resolver: zodResolver(
      insertEmployeeSchema.pick({ employmentType: true }).passthrough()
    ),
    defaultValues: {
      position: "",
      department: "",
      departmentId: null,
      jobTitleId: null,
      locationId: null,
      employeeClassId: null,
      divisionId: null,
      hireDate: "",
      employmentType: "",
      employeeId: "",
      scheduledHours: "",
      compensationType: "",
      compensationAmount: "",
      compensationEffectiveDate: "",
      payCycle: "",
      status: "active",
      email: "",
      workPhone: "",
      licenseNumber: "",
      managerEmployeeId: null,
    },
  });


  type DependentFormValues = {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    sex: string;
    relationship: string;
    ssn: string;
    address: string;
    isEligible: boolean;
  };
  const dependentForm = useForm<DependentFormValues>({
    resolver: zodResolver(
      (insertDependentSchema as any).omit({ employeeId: true })
    ),
    defaultValues: {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      sex: "",
      relationship: "",
      ssn: "",
      address: "",
      isEligible: true,
    },
  });

  const contactHistoryForm = useForm({
    resolver: zodResolver(contactInteractionFormSchema),
    defaultValues: {
      clientId: employee?.clientId || null,
      prospectId: employee?.prospectId || null,
      type: "",
      subject: "",
      // Use full datetime-local default for the input control
      contactDate: getCurrentLocalDateTime(),
      notes: "",
      outcome: "",
      nextFollowUp: "",
    },
  });

  type EnrollmentFormValues = {
    employeeId: number;
    clientId: number;
    planId: string;
    dentalPlanId: string;
    visionPlanId: string;
    effectiveDate: string;
    employeeWaived: boolean;
    employeeWaiverReason: string;
    selectedDependents: any[];
    dependentWaivers: Record<number, boolean>;
    medicalEnrollees: number[];
    dentalEnrollees: number[];
    visionEnrollees: number[];
    medicalAction?: "enroll" | "waive";
    dentalAction?: "enroll" | "waive";
    visionAction?: "enroll" | "waive";
    medicalWaiverReason?: string;
    dentalWaiverReason?: string;
    visionWaiverReason?: string;
  };

  const enrollmentForm = useForm<EnrollmentFormValues>({
    defaultValues: {
      employeeId: parseInt(employeeId || "0"),
      clientId: employee?.clientId || 0,
      planId: "",
      dentalPlanId: "",
      visionPlanId: "",
      effectiveDate: new Date().toISOString().split("T")[0],
      employeeWaived: false,
      employeeWaiverReason: "",
      selectedDependents: [],
      dependentWaivers: {},
      medicalEnrollees: [],
      dentalEnrollees: [],
      visionEnrollees: [],
    },
  });

  const watchedPlanId = enrollmentForm.watch("planId");
  const watchedEmployeeWaived = enrollmentForm.watch("employeeWaived");
  const watchedSelectedDependents = enrollmentForm.watch("selectedDependents");
  const watchedDependentWaivers = enrollmentForm.watch("dependentWaivers");

  // Reset forms with employee data when it loads
  React.useEffect(() => {
    if (employee) {
      setRevealedSsn(undefined);
      setShowSSN(false);
      const addressDefaults = normalizeAddressFields({
        address1: employee.address1,
        address2: employee.address2,
        city: employee.city,
        state: employee.state,
        zipCode: employee.zipCode,
        county: employee.county,
        country: employee.country,
      });

      profileForm.reset({
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        personalEmail: (employee as any)?.personalEmail || "",
        phone: employee.phone || "",
        dateOfBirth: employee.dateOfBirth
          ? (parseDateLocal(employee.dateOfBirth) ?? new Date(employee.dateOfBirth)).toISOString().split("T")[0]
          : "",
        // Option values are lowercase; older rows were saved capitalized.
        sex: (employee.sex || "").trim().toLowerCase(),
        maritalStatus: (employee.maritalStatus || "").trim().toLowerCase(),
        ssn: employee.ssn || "",
        tobaccoUser: employee.tobaccoUser || "",
        disabled: employee.disabled || "",
        ...addressDefaults,
        status: normalizeStatus(employee.status) || "active",
      });

      employmentForm.reset({
        position: employee.position || "",
        department: employee.department || "",
        departmentId: (employee as any)?.departmentId ?? null,
        jobTitleId: (employee as any)?.jobTitleId ?? null,
        locationId: (employee as any)?.locationId ?? null,
        employeeClassId: (employee as any)?.employeeClassId ?? null,
        divisionId: (employee as any)?.divisionId ?? null,
        hireDate: employee.hireDate
          ? (parseDateLocal(employee.hireDate) ?? new Date(employee.hireDate)).toISOString().split("T")[0]
          : "",
        employmentType: normalizeEmploymentValue(employee.employmentType),
        employeeId: employee.employeeId || "",
        scheduledHours: employee.scheduledHours || "",
        compensationType: normalizeEmploymentValue(employee.compensationType),
        compensationAmount: employee.compensationAmount || "",
        // Blank on purpose: the date is only sent when the broker records a raise.
        compensationEffectiveDate: "",
        payCycle: normalizeEmploymentValue(employee.payCycle),
        status: normalizeStatus(employee.status) || "",
        licenseNumber: (employee as any)?.licenseNumber || "",
        email: employee.email || "",
        workPhone: (employee as any)?.workPhone || "",
        managerEmployeeId: (employee as any)?.managerEmployeeId ?? null,
      });
    }
  }, [employee, profileForm, employmentForm]);


  if (isLoadingEmployee) {
    return (
      <div className="flex items-center justify-center h-64">Loading...</div>
    );
  }

  if (!employee) {
    return (
      <div className="flex items-center justify-center h-64">
        Employee not found
      </div>
    );
  }

  const handleProfileSubmit = (data: any) => {
    const addressLine = composeAddressLine(data);
    const payload = {
      ...data,
      address: addressLine || null,
      address1: data.address1 || null,
      address2: data.address2 || null,
      city: data.city || null,
      state: data.state || null,
      zipCode: data.zipCode || null,
      county: data.county || null,
      country: data.country || null,
    };
    // Unchanged (masked or same as revealed) SSN is not sent, so it can't overwrite the stored one.
    if (ssnToSave(payload.ssn, revealedSsn) === undefined) delete payload.ssn;

    // Compute field-level diffs for history
    const changes: Record<string, { from: any; to: any }> = {};
    const fieldsToTrack = [
      'firstName', 'lastName', 'personalEmail', 'phone', 'dateOfBirth',
      'sex', 'maritalStatus', 'ssn', 'tobaccoUser', 'disabled',
      'address1', 'address2', 'city', 'state', 'zipCode', 'county', 'country'
    ];
    fieldsToTrack.forEach((field) => {
      if (field === 'ssn' && !('ssn' in payload)) return;
      const oldVal = (employee as any)?.[field];
      const newVal = payload[field];
      if (oldVal !== newVal && (oldVal || newVal)) {
        changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
      }
    });
    (window as any).__profileChanges = changes;
    
    updateEmployeeMutation.mutate(payload);
  };

  // Without this, a validation failure (e.g. blank employmentType) silently blocks Save.
  const handleEmploymentInvalid = (errors: Record<string, { message?: string }>) => {
    const firstMessage = Object.values(errors)[0]?.message;
    toast({
      title: "Can't save",
      description: firstMessage || "Please fix the highlighted field before saving.",
      variant: "destructive",
    });
  };

  const handleEmploymentSubmit = async (data: any) => {
    // Look up the names from the selected IDs to populate legacy text fields
    const selectedJobTitle = data.jobTitleId
      ? orgJobTitles.find((jt: any) => jt.id === data.jobTitleId)
      : null;
    const selectedDepartment = data.departmentId
      ? orgDepartments.find((d: any) => d.id === data.departmentId)
      : null;

    // Map selected org IDs while preserving legacy text fields
    const payload = {
      ...data,
      departmentId: data.departmentId ?? null,
      jobTitleId: data.jobTitleId ?? null,
      locationId: data.locationId ?? null,
      employeeClassId: data.employeeClassId ?? null,
      divisionId: data.divisionId ?? null,
      // Update legacy text fields with the selected names
      position: selectedJobTitle?.name || data.position || null,
      department: selectedDepartment?.name || data.department || null,
    };
    // Only send an effective date when the broker set one; a blank must not
    // clear the stored date (that would log a spurious compensation change).
    if (!payload.compensationEffectiveDate) delete (payload as any).compensationEffectiveDate;
    
    // Detect employment changes that require verification
    const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : undefined;
    const currentEnrollments = enrollments?.filter((e: any) => 
      e.status === 'active' || e.status === 'Active'
    ) || [];
    
    const significantChanges = detectEmploymentChanges(
      employee,
      payload,
      employeeName,
      currentEnrollments
    );
    
    // If there are significant changes, require verification
    if (significantChanges.length > 0) {
      const results = await verifyChanges(significantChanges);
      
      // If user cancelled, don't proceed
      if (!results) {
        return;
      }
      
      // Store verification results with the payload for audit trail
      (payload as any).__verificationResults = results;
    }
    
    // Compute field-level diffs for history
    const changes: Record<string, { from: any; to: any }> = {};
    const fieldsToTrack = [
      'position', 'department', 'hireDate', 'employmentType', 'employeeId',
      'scheduledHours', 'compensationType', 'compensationAmount', 'payCycle', 'status', 'licenseNumber', 'managerEmployeeId'
    ];
    fieldsToTrack.forEach((field) => {
      const oldVal = (employee as any)?.[field];
      const newVal = payload[field];
      if (oldVal !== newVal && (oldVal || newVal)) {
        changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
      }
    });
    (window as any).__employmentChanges = changes;
    
    console.log("=== EMPLOYMENT SUBMIT DEBUG ===");
    console.log("Form data:", data);
    console.log("Payload being sent:", payload);
    console.log("employmentType in data:", data.employmentType);
    console.log("employmentType in payload:", payload.employmentType);
    updateEmployeeMutation.mutate(payload);
  };

  const handleEmergencyContactSubmit = (data: any) => {
    if (editingEmergencyContact) {
      // Update existing emergency contact
      const changes: Record<string, { from: any; to: any }> = {};
      const fieldsToTrack = [
        'name', 'phone', 'email', 'relationship', 'contactType',
        'address1', 'address2', 'city', 'state', 'zipCode', 'county', 'country'
      ];
      fieldsToTrack.forEach((field) => {
        const oldVal = editingEmergencyContact[field];
        const newVal = data[field];
        if (oldVal !== newVal && (oldVal || newVal)) {
          changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
        }
      });
      (window as any).__emergencyContactChanges = changes;
      (window as any).__emergencyContactUpdateName = data.name;
      
      updateEmergencyContactMutation.mutate({
        id: editingEmergencyContact.id,
        data,
      });
    } else {
      // Create new emergency contact
      (window as any).__emergencyContactAddName = data.name;
      createEmergencyContactMutation.mutate(data);
    }
  };

  const handleEditEmergencyContact = (contact: EmergencyContact) => {
    setEditingEmergencyContact(contact);
    setShowAddEmergencyContact(true);
  };

  const handleDeleteEmergencyContact = (contactId: number) => {
    // Store contact name for history
    const contact = emergencyContacts.find((c: any) => c.id === contactId);
    if (contact) {
      (window as any).__emergencyContactDeleteName = contact.name;
    }
    deleteEmergencyContactMutation.mutate(contactId);
  };

  const handleAddDependent = (data: any) => {
    createDependentMutation.mutate(data);
    dependentForm.reset();
  };

  const handleDeleteDependent = (dependentId: number) => {
    deleteDependentMutation.mutate(dependentId);
  };

  const handleAddContactHistory = async (data: any) => {
    try {
      // Normalize payload for server schema (dates as ISO strings; omit empty follow-up)
      const payload: any = {
        ...data,
        contactDate: data?.contactDate
          ? (parseDateLocal(data.contactDate) ?? new Date()).toISOString()
          : new Date().toISOString(),
        nextFollowUp:
          data?.nextFollowUp && String(data.nextFollowUp).trim() !== ""
            ? (parseDateLocal(data.nextFollowUp) ?? new Date(data.nextFollowUp)).toISOString()
            : undefined,
      };

      const response = await fetch(
        `/api/employees/${employeeId}/contact-interactions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error("Failed to add contact interaction");
      queryClientInstance.invalidateQueries({
        queryKey: ["/api/employees", employeeId, "contact-interactions"],
      });
      toast({ title: "Contact interaction added successfully" , variant: "success"});
      setShowAddContactHistory(false);
      contactHistoryForm.reset();
    } catch (error) {
      toast({
        title: "Failed to add contact interaction",
        variant: "destructive",
      });
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
  };

  // The top Save button should trigger the active tab's form so we avoid duplicating submit buttons.
  const handleSaveClick = () => {
    if (!isEditing) {
      return;
    }

    if (activeTab === "profile" && profileFormRef.current) {
      profileFormRef.current.requestSubmit();
      return;
    }

    if (activeTab === "employment" && employmentFormRef.current) {
      employmentFormRef.current.requestSubmit();
    }
  };

  const handleOpenDeleteDialog = () => {
    setShowDeleteConfirmation(true);
  };

  const handleAddEnrollment = (data: any) => {
    const finalCoverageTier = data.employeeWaived
      ? "Employee Only"
      : calculateFinalCoverageTier(
          data.selectedDependents || [],
          data.dependentWaivers || {}
        );

    // Get eligible dependents for rate calculation
    const eligibleDependents = (data.selectedDependents || []).filter(
      (dep: any) => !data.dependentWaivers?.[dep.id]
    );
    const selectedDependentIds = eligibleDependents.map((d: any) => d.id);

    // Create enrollments for selected plans
    const enrollmentsToCreate = [];

    // Medical plan enrollment
    if (data.planId) {
      const planId = parseInt(data.planId);
      const totalPremium = getPlanRate(
        planId,
        finalCoverageTier,
        eligibleDependents
      );
      const { employeePremium, employerPremium } = calculateContributions(
        planId,
        finalCoverageTier,
        totalPremium
      );

      enrollmentsToCreate.push({
        employeeId: data.employeeId,
        clientId: data.clientId,
        planId: planId,
        coverageTier: finalCoverageTier,
        effectiveDate: parseDateLocal(data.effectiveDate) ?? new Date(),
        status: data.employeeWaived ? "waived" : "active",
        // Premiums are computed dynamically server-side; do not persist
        waivedCoverage: data.employeeWaived || false,
        waiverReason: data.employeeWaiverReason || null,
        enrollmentDate: new Date(),
        lastModified: new Date(),
        selectedDependentIds,
      });
    }

    // Dental plan enrollment
    if (data.dentalPlanId) {
      const planId = parseInt(data.dentalPlanId);
      const totalPremium = getPlanRate(
        planId,
        finalCoverageTier,
        eligibleDependents
      );
      const { employeePremium, employerPremium } = calculateContributions(
        planId,
        finalCoverageTier,
        totalPremium
      );

      enrollmentsToCreate.push({
        employeeId: data.employeeId,
        clientId: data.clientId,
        planId: planId,
        coverageTier: finalCoverageTier,
        effectiveDate: parseDateLocal(data.effectiveDate) ?? new Date(),
        status: "active",
        // Premiums are computed dynamically server-side; do not persist
        waivedCoverage: false,
        waiverReason: null,
        enrollmentDate: new Date(),
        lastModified: new Date(),
        selectedDependentIds,
      });
    }

    // Vision plan enrollment
    if (data.visionPlanId) {
      const planId = parseInt(data.visionPlanId);
      const totalPremium = getPlanRate(
        planId,
        finalCoverageTier,
        eligibleDependents
      );
      const { employeePremium, employerPremium } = calculateContributions(
        planId,
        finalCoverageTier,
        totalPremium
      );

      enrollmentsToCreate.push({
        employeeId: data.employeeId,
        clientId: data.clientId,
        planId: planId,
        coverageTier: finalCoverageTier,
        effectiveDate: parseDateLocal(data.effectiveDate) ?? new Date(),
        status: "active",
        // Premiums are computed dynamically server-side; do not persist
        waivedCoverage: false,
        waiverReason: null,
        enrollmentDate: new Date(),
        lastModified: new Date(),
        selectedDependentIds,
      });
    }

    // Create all enrollments
    if (enrollmentsToCreate.length > 0) {
      enrollmentsToCreate.forEach((enrollment) => {
        addEnrollmentMutation.mutate(enrollment);
      });
    }
  };

  const calculateAge = (dateOfBirth: string | Date | null) => {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = parseDateLocal(dateOfBirth as any);
    if (!birthDate) return 0;
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const getPlanRate = (
    planId: number,
    coverageTier: string,
    selectedDependents: any[] = []
  ) => {
    // ONLY use client-specific rates from localStorage - no global fallbacks
    const ratesStorageKey = `client_${clientId}_plan_${planId}_rates`;
    const savedRates = localStorage.getItem(ratesStorageKey);

    if (!savedRates) {
      // No client-specific rates configured - cannot calculate premium
      return 0;
    }

    const savedData = JSON.parse(savedRates);
    const ageBandRates = savedData.ageBandRates || {};
    const tierRates = savedData.tierRates || {};
    const ratingMethod = savedData.ratingMethod || "age_based";
    const ageBandType = savedData.ageBandType || "all_ages";

    if (ratingMethod === "age_based") {
      // Use age-based rating from client-specific configuration ONLY
      if (Object.keys(ageBandRates).length === 0) {
        // No age band rates configured for this client
        return 0;
      }

      const employeeAge = calculateAge(employee?.dateOfBirth);
      let totalRate = 0;

      // Function to find rate for a given age based on age band configuration
      const findAgeRate = (age: number) => {
        // For age band grouping, calculate the appropriate age band
        if (ageBandType === "five_year") {
          // Group into 5-year age bands
          let ageBand = "";
          if (age >= 0 && age <= 14) ageBand = "0-14";
          else if (age >= 15 && age <= 19) ageBand = "15-19";
          else if (age >= 20 && age <= 24) ageBand = "20-24";
          else if (age >= 25 && age <= 29) ageBand = "25-29";
          else if (age >= 30 && age <= 34) ageBand = "30-34";
          else if (age >= 35 && age <= 39) ageBand = "35-39";
          else if (age >= 40 && age <= 44) ageBand = "40-44";
          else if (age >= 45 && age <= 49) ageBand = "45-49";
          else if (age >= 50 && age <= 54) ageBand = "50-54";
          else if (age >= 55 && age <= 59) ageBand = "55-59";
          else if (age >= 60 && age <= 64) ageBand = "60-64";
          else if (age >= 65) ageBand = "64+";

          if (ageBandRates[ageBand]) {
            return parseFloat(String(ageBandRates[ageBand]));
          }
        } else if (ageBandType === "ten_year") {
          // Group into 10-year age bands
          let ageBand = "";
          if (age >= 0 && age <= 14) ageBand = "0-14";
          else if (age >= 15 && age <= 24) ageBand = "15-24";
          else if (age >= 25 && age <= 34) ageBand = "25-34";
          else if (age >= 35 && age <= 44) ageBand = "35-44";
          else if (age >= 45 && age <= 54) ageBand = "45-54";
          else if (age >= 55 && age <= 64) ageBand = "55-64";
          else if (age >= 65) ageBand = "64+";

          if (ageBandRates[ageBand]) {
            return parseFloat(String(ageBandRates[ageBand]));
          }
        } else {
          // All ages or individual age matching
          // First try exact age match
          if (ageBandRates[age.toString()]) {
            return parseFloat(String(ageBandRates[age.toString()]));
          }

          // Then try age ranges
          for (const [ageKey, rate] of Object.entries(ageBandRates)) {
            if (ageKey.includes("-")) {
              const [minAge, maxAge] = ageKey.split("-").map(Number);
              if (age >= minAge && age <= maxAge) {
                return parseFloat(String(rate));
              }
            } else if (ageKey === "64+" && age >= 64) {
              return parseFloat(String(rate));
            }
          }
        }

        // Fallback to closest age or 64+ rate
        return parseFloat(
          String(ageBandRates["64+"] || ageBandRates["65+"] || "0")
        );
      };

      // Get employee rate based on age
      totalRate += findAgeRate(employeeAge);

      // Add dependent rates if applicable
      if (selectedDependents && selectedDependents.length > 0) {
        selectedDependents.forEach((dependent: any) => {
          const depAge = calculateAge(dependent.dateOfBirth);
          totalRate += findAgeRate(depAge);
        });
      }

      return totalRate;
    } else {
      // Use tier-based rating from client-specific configuration ONLY
      if (Object.keys(tierRates).length === 0) {
        // No tier rates configured for this client
        return 0;
      }

      const tierRateMap: Record<
        | "Employee Only"
        | "Employee + Spouse"
        | "Employee + Child"
        | "Employee + Children"
        | "Employee + Family",
        number
      > = {
        "Employee Only": parseFloat(String(tierRates.employee_only || "0")),
        "Employee + Spouse": parseFloat(
          String(tierRates.employee_spouse || "0")
        ),
        "Employee + Child": parseFloat(
          String(tierRates.employee_children || "0")
        ),
        "Employee + Children": parseFloat(
          String(tierRates.employee_children || "0")
        ),
        "Employee + Family": parseFloat(String(tierRates.family || "0")),
      };

      return tierRateMap[coverageTier as keyof typeof tierRateMap] || 0;
    }
  };

  // New tier-based contribution calculation for plans with tier rates (like vision)
  const calculateTierBasedContributions = (
    planContribution: any,
    coverageTier: string,
    totalPremium: number,
    isEmployerContribution: boolean
  ) => {
    // For tier-based contributions, we use the contribution structure differently
    // The employer percentage applies to the employee portion, dependents get separate percentage

    const employeeContributionPercent = parseFloat(
      planContribution.employeeContribution || "0"
    );
    const spouseContributionPercent = parseFloat(
      planContribution.spouseContribution || "0"
    );
    const childContributionPercent = parseFloat(
      planContribution.oneChildContribution || "0"
    );

    // For vision plan with tier structure: employer pays 100% employee, 50% dependents
    // This means employee contribution is 0%, dependent contributions are 50%

    let employeePremium = 0;
    let employerPremium = 0;

    if (coverageTier === "Employee Only") {
      // Just employee - use employee contribution percentage
      if (isEmployerContribution) {
        employerPremium = totalPremium * (employeeContributionPercent / 100);
        employeePremium = totalPremium - employerPremium;
      } else {
        employeePremium = totalPremium * (employeeContributionPercent / 100);
        employerPremium = totalPremium - employeePremium;
      }
    } else {
      // For tier plans, we need to break down the premium by component
      // The tier rates represent the full premium for each tier level
      // We need to calculate the employer/employee split based on the contribution structure

      // Get the base tier rates to calculate component costs
      const ratesStorageKey = `client_${clientId}_plan_${planContribution.planId}_rates`;
      const savedRates = localStorage.getItem(ratesStorageKey);

      if (savedRates) {
        const savedData = JSON.parse(savedRates);
        const tierRates = savedData.tierRates || {};

        const employeeOnlyRate = parseFloat(tierRates.employee_only || "0");
        const currentTierRate = parseFloat(
          tierRates[
            coverageTier.toLowerCase().replace(/\s+/g, "_").replace(/\+/g, "")
          ] || totalPremium.toString()
        );

        // Employee portion (always same regardless of tier)
        const employeePortion = employeeOnlyRate;
        const dependentPortion = currentTierRate - employeeOnlyRate;

        // Apply contribution percentages
        if (isEmployerContribution) {
          // Employer pays specified percentage
          const employerEmployeePortion =
            employeePortion * (employeeContributionPercent / 100);
          const employerDependentPortion =
            dependentPortion * (spouseContributionPercent / 100); // Use spouse % for all dependents

          employerPremium = employerEmployeePortion + employerDependentPortion;
          employeePremium = totalPremium - employerPremium;
        } else {
          // Employee pays specified percentage
          const employeeEmployeePortion =
            employeePortion * (employeeContributionPercent / 100);
          const employeeDependentPortion =
            dependentPortion * (spouseContributionPercent / 100);

          employeePremium = employeeEmployeePortion + employeeDependentPortion;
          employerPremium = totalPremium - employeePremium;
        }
      } else {
        // Fallback to simple calculation if no tier rates available
        if (isEmployerContribution) {
          employerPremium = totalPremium * (employeeContributionPercent / 100);
          employeePremium = totalPremium - employerPremium;
        } else {
          employeePremium = totalPremium * (employeeContributionPercent / 100);
          employerPremium = totalPremium - employeePremium;
        }
      }
    }

    return {
      employeePremium: Math.max(0, employeePremium),
      employerPremium: Math.max(0, employerPremium),
    };
  };

  // Age-based contribution calculation for complex medical plans
  const calculateAgeBasedContributions = (
    planContribution: any,
    coverageTier: string,
    totalPremium: number,
    isEmployerContribution: boolean,
    savedRates: string | null
  ) => {
    if (!savedRates) {
      return calculateSimpleContributions(
        planContribution,
        coverageTier,
        totalPremium,
        isEmployerContribution
      );
    }

    const savedData = JSON.parse(savedRates);
    const ageBandRates = savedData.ageBandRates || {};
    const ageBandType = savedData.ageBandType || "all_ages";

    // Calculate contributions based on individual component rates
    let employeePremium = 0;
    let employerPremium = 0;

    // Calculate employee premium component
    const employeeAge = calculateAge(employee?.dateOfBirth);
    const employeeRate = getAgeBasedRate(
      employeeAge,
      ageBandRates,
      ageBandType
    );

    // Get employer contribution percentage for employee
    const employeeContributionPercent = parseFloat(
      planContribution.employeeContribution || "0"
    );

    if (isEmployerContribution) {
      employerPremium += employeeRate * (employeeContributionPercent / 100);
      employeePremium +=
        employeeRate * ((100 - employeeContributionPercent) / 100);
    } else {
      employeePremium += employeeRate * (employeeContributionPercent / 100);
      employerPremium +=
        employeeRate * ((100 - employeeContributionPercent) / 100);
    }

    // Calculate dependent premiums if applicable
    if (coverageTier !== "Employee Only") {
      // Find dependents who should be included based on coverage tier and form selections
      const selectedDeps =
        (
          enrollmentForm.watch("medicalEnrollees") as number[] | undefined
        )?.filter((id: number) => id !== (employee?.id as number)) || [];
      const includedDependents = dependents.filter((dep: any) =>
        selectedDeps.includes(dep.id)
      );

      includedDependents.forEach((dep: any) => {
        const depAge = calculateAge(dep.dateOfBirth);
        const depRate = getAgeBasedRate(depAge, ageBandRates, ageBandType);

        // Determine dependent contribution percentage based on relationship
        let depContributionPercent = 0;
        if (dep.relationship.toLowerCase().includes("spouse")) {
          depContributionPercent = parseFloat(
            planContribution.spouseContribution || "0"
          );
        } else {
          // Child
          depContributionPercent = parseFloat(
            planContribution.oneChildContribution || "0"
          );
        }

        if (isEmployerContribution) {
          employerPremium += depRate * (depContributionPercent / 100);
          employeePremium += depRate * ((100 - depContributionPercent) / 100);
        } else {
          employeePremium += depRate * (depContributionPercent / 100);
          employerPremium += depRate * ((100 - depContributionPercent) / 100);
        }
      });
    }

    return {
      employeePremium: Math.max(0, employeePremium),
      employerPremium: Math.max(0, employerPremium),
    };
  };

  const calculateContributions = (
    planId: number,
    coverageTier: string,
    totalPremium: number
  ) => {
    // Get all contribution rows for this plan
    const contribRows = (planContributions as any[]).filter(
      (pc: any) => pc.planId === planId
    );

    if (contribRows.length === 0) {
      // Default contribution split if no plan-specific data
      const employeePremium = totalPremium * 0.3;
      const employerPremium = totalPremium * 0.7;
      return { employeePremium, employerPremium };
    }

    const isEmployerContribution = contribRows[0]?.payer === "employer";

    // Build a lookup by contributionTypeValue → percentage for legacy helper functions
    const pctByTier: Record<string, string> = {};
    for (const row of contribRows) {
      if (row.contributionTypeValue && row.percentage != null) {
        pctByTier[row.contributionTypeValue] = String(row.percentage);
      }
    }

    // Create a legacy-shaped object that the helper functions expect
    const planContribution = {
      planId,
      payer: contribRows[0]?.payer,
      employeeContribution: pctByTier["employee_only"] || "0",
      spouseContribution: pctByTier["employee_spouse"] || "0",
      oneChildContribution: pctByTier["employee_children"] || "0",
      twoChildrenContribution: pctByTier["employee_children"] || "0",
      threeChildrenContribution: pctByTier["family"] || "0",
    };

    // Get client-specific rates from localStorage to determine if this is tier-based or age-based
    const ratesStorageKey = `client_${clientId}_plan_${planId}_rates`;
    const savedRates = localStorage.getItem(ratesStorageKey);

    // Check if this plan uses tier-based rates (not age-based)
    let useTierBasedCalculation = true;
    if (savedRates) {
      const savedData = JSON.parse(savedRates);
      const ratingMethod = savedData.ratingMethod || "age_based";
      const ageBandRates = savedData.ageBandRates || {};

      // If it's age-based and has age band rates, use the complex calculation
      if (
        ratingMethod === "age_based" &&
        Object.keys(ageBandRates).length > 0
      ) {
        useTierBasedCalculation = false;
      }
    }

    // For tier-based plans (like vision), use simple tier calculation
    if (useTierBasedCalculation) {
      return calculateTierBasedContributions(
        planContribution,
        coverageTier,
        totalPremium,
        isEmployerContribution
      );
    }

    // For age-based plans, use the complex age-based calculation
    return calculateAgeBasedContributions(
      planContribution,
      coverageTier,
      totalPremium,
      isEmployerContribution,
      savedRates
    );
  };

  // Helper function to get age-based rate
  const getAgeBasedRate = (
    age: number,
    ageBandRates: any,
    ageBandType: string
  ) => {
    if (ageBandType === "five_year") {
      // Group into 5-year age bands
      let ageBand = "";
      if (age >= 0 && age <= 14) ageBand = "0-14";
      else if (age >= 15 && age <= 19) ageBand = "15-19";
      else if (age >= 20 && age <= 24) ageBand = "20-24";
      else if (age >= 25 && age <= 29) ageBand = "25-29";
      else if (age >= 30 && age <= 34) ageBand = "30-34";
      else if (age >= 35 && age <= 39) ageBand = "35-39";
      else if (age >= 40 && age <= 44) ageBand = "40-44";
      else if (age >= 45 && age <= 49) ageBand = "45-49";
      else if (age >= 50 && age <= 54) ageBand = "50-54";
      else if (age >= 55 && age <= 59) ageBand = "55-59";
      else if (age >= 60 && age <= 64) ageBand = "60-64";
      else if (age >= 65) ageBand = "64+";

      if (ageBandRates[ageBand]) {
        return parseFloat(ageBandRates[ageBand]);
      }
    } else if (ageBandType === "ten_year") {
      // Group into 10-year age bands
      let ageBand = "";
      if (age >= 0 && age <= 14) ageBand = "0-14";
      else if (age >= 15 && age <= 24) ageBand = "15-24";
      else if (age >= 25 && age <= 34) ageBand = "25-34";
      else if (age >= 35 && age <= 44) ageBand = "35-44";
      else if (age >= 45 && age <= 54) ageBand = "45-54";
      else if (age >= 55 && age <= 64) ageBand = "55-64";
      else if (age >= 65) ageBand = "64+";

      if (ageBandRates[ageBand]) {
        return parseFloat(ageBandRates[ageBand]);
      }
    } else {
      // Individual age rates or simple age ranges
      if (ageBandRates[age.toString()]) {
        return parseFloat(ageBandRates[age.toString()]);
      }

      // Try age ranges
      for (const [ageKey, rate] of Object.entries(ageBandRates)) {
        if (ageKey.includes("-")) {
          const [minAge, maxAge] = ageKey.split("-").map(Number);
          if (age >= minAge && age <= maxAge) {
            return parseFloat(rate as string);
          }
        } else if (ageKey === "64+" && age >= 64) {
          return parseFloat(rate as string);
        }
      }
    }

    // Fallback to base rate or 0
    return 0;
  };

  const calculateSimpleContributions = (
    planContribution: any,
    coverageTier: string,
    totalPremium: number,
    isEmployerContribution: boolean
  ) => {
    const tierKey = coverageTier
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/\+/g, "");
    let contributionPercentage = 0;

    switch (tierKey) {
      case "employeeonly":
        contributionPercentage = parseFloat(
          planContribution.employeeContribution || "0"
        );
        break;
      case "employeespouse":
        contributionPercentage = parseFloat(
          planContribution.spouseContribution || "0"
        );
        break;
      case "employeechild":
        contributionPercentage = parseFloat(
          planContribution.oneChildContribution || "0"
        );
        break;
      case "employeechildren":
        contributionPercentage = parseFloat(
          planContribution.twoChildrenContribution || "0"
        );
        break;
      case "employeefamily":
        contributionPercentage = parseFloat(
          planContribution.threeChildrenContribution || "0"
        );
        break;
      default:
        contributionPercentage = parseFloat(
          planContribution.employeeContribution || "0"
        );
    }

    let employeePremium = 0;
    let employerPremium = 0;

    if (isEmployerContribution) {
      employerPremium = totalPremium * (contributionPercentage / 100);
      employeePremium = totalPremium - employerPremium;
    } else {
      employeePremium = totalPremium * (contributionPercentage / 100);
      employerPremium = totalPremium - employeePremium;
    }

    return { employeePremium, employerPremium };
  };

  const calculateFinalCoverageTier = (
    selectedDependents: any[],
    dependentWaivers: any
  ) => {
    if (!selectedDependents || selectedDependents.length === 0) {
      return "Employee Only";
    }

    const eligibleDependents = selectedDependents.filter(
      (dep) => !dependentWaivers[dep.id]
    );

    if (eligibleDependents.length === 0) {
      return "Employee Only";
    }

    const hasSpouse = eligibleDependents.some(
      (dep) => dep.relationship.toLowerCase() === "spouse"
    );
    const childCount = eligibleDependents.filter((dep) =>
      dep.relationship.toLowerCase().includes("child")
    ).length;

    if (hasSpouse && childCount > 0) {
      return "Employee + Family";
    } else if (hasSpouse) {
      return "Employee + Spouse";
    } else if (childCount === 1) {
      return "Employee + Child";
    } else if (childCount > 1) {
      return "Employee + Children";
    }

    return "Employee Only";
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs and Navigation */}
      <div className="flex items-center justify-between">
        <nav className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
          {(() => {
            const pathname =
              typeof window !== "undefined" ? window.location.pathname : "";
            const isAgencyContext = pathname.startsWith("/agency/");
            if (isAgencyContext) {
              return (
                <>
                  <button
                    onClick={() => setLocation("/agency?tab=employees")}
                    className="hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Agency
                  </button>
                  <ChevronRight className="h-4 w-4" />
                  <button
                    onClick={() => setLocation("/agency?tab=employees")}
                    className="hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Employees
                  </button>
                  <ChevronRight className="h-4 w-4" />
                  <EmployeeNameSwitcher
                    employees={sortedEmployees}
                    currentId={employee.id}
                    currentName={`${employee.firstName} ${employee.lastName}`}
                    onSelect={navigateToEmployee}
                  />
                </>
              );
            }
            return (
              <>
                <button
                  onClick={() => setLocation("/clients")}
                  className="hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Clients
                </button>
                <ChevronRight className="h-4 w-4" />
                <button
                  onClick={() => setLocation(`/clients/${client?.id}`)}
                  className="hover:text-gray-700 dark:hover:text-gray-300"
                >
                  {client?.name}
                </button>
                <ChevronRight className="h-4 w-4" />
                <button
                  onClick={() =>
                    setLocation(`/clients/${client?.id}?tab=employees`)
                  }
                  className="hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Employees
                </button>
                <ChevronRight className="h-4 w-4" />
                <EmployeeNameSwitcher
                  employees={sortedEmployees}
                  currentId={employee.id}
                  currentName={`${employee.firstName} ${employee.lastName}`}
                  onSelect={navigateToEmployee}
                />
              </>
            );
          })()}
        </nav>

        {/* Employee Navigation */}
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!previousEmployee}
                  onClick={() => previousEmployee && navigateToEmployee(previousEmployee.id)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{previousEmployee ? `Previous: ${previousEmployee.firstName} ${previousEmployee.lastName}` : "No previous employee"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {currentIndex + 1} / {sortedEmployees.length}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!nextEmployee}
                  onClick={() => nextEmployee && navigateToEmployee(nextEmployee.id)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{nextEmployee ? `Next: ${nextEmployee.firstName} ${nextEmployee.lastName}` : "No next employee"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <ProfilePictureUpload
                  currentPicture={employee.profilePicture}
                  firstName={employee.firstName}
                  lastName={employee.lastName}
                  size="xl"
                  uploadEndpoint={`/api/employees/${employee.id}/profile-picture`}
                  deleteEndpoint={`/api/employees/${employee.id}/profile-picture`}
                  fetchUrlEndpoint={`/api/employees/${employee.id}/profile-picture-url`}
                  onPictureChange={async () => {
                    // Refetch employee data after upload/delete
                    await queryClient.invalidateQueries({
                      queryKey: [`/api/employees/${employee.id}`],
                    });
                  }}
                />
              </div>
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {employee.firstName} {employee.lastName}
                  </h1>
                  <Badge
                    variant={
                      employee.status === "active" ? "default" : "secondary"
                    }
                    className={
                      employee.status === "pending_activation"
                        ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
                        : undefined
                    }
                  >
                    {formatStatus(employee.status)}
                  </Badge>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {employee.position} • {employee.department}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Employee ID: {employee.employeeId || "Not assigned"}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              {/* View/Send Login button - always visible */}
              <PermissionGate anyOf={["clients:create", "prospects:create"]}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block">
                        <Button
                          onClick={() => setShowLoginModal(true)}
                          variant="outline"
                          className="gap-2"
                        >
                          <KeyRound className="h-4 w-4" />
                          View/Send Login
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View or send login credentials</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </PermissionGate>
              {!isEditing && canDeleteData?.canDelete && (
                <PermissionButton
                  permission="clients:edit"
                  whenDenied="hide"
                  onClick={handleOpenDeleteDialog}
                  variant="destructive"
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Employee
                </PermissionButton>
              )}
              {/* Terminate action (available when not already terminated) */}
              {employee.status !== "terminated" && (
                <PermissionButton
                  permission="clients:edit"
                  whenDenied="hide"
                  variant="destructive"
                  onClick={handleTerminate}
                  disabled={terminateMutation.isPending}
                >
                  {terminateMutation.isPending ? "Terminating..." : "Terminate"}
                </PermissionButton>
              )}
              {/* Re-Hire action (available only for terminated employees) */}
              {employee.status === "terminated" && (
                <PermissionButton
                  permission="clients:edit"
                  whenDenied="hide"
                  variant="default"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleRehire}
                  disabled={rehireMutation.isPending}
                >
                  {rehireMutation.isPending ? "Re-hiring..." : "Re-Hire"}
                </PermissionButton>
              )}
            </div>
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as typeof EMPLOYEE_TABS[number])}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-7 bg-[#eff6fe] dark:bg-gray-800">
          <TabsTrigger value="profile" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white">Profile</TabsTrigger>
          <TabsTrigger value="employment" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white">Employment</TabsTrigger>
          <TabsTrigger value="benefits" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white">Benefits</TabsTrigger>
          <TabsTrigger value="dependents" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white">Dependents</TabsTrigger>
          <TabsTrigger value="aca" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white">ACA</TabsTrigger>
          <TabsTrigger value="history" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white">History</TabsTrigger>
          <TabsTrigger value="documents" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white">Documents</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="bg-blue-50 dark:bg-blue-950/20 flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Personal Information
              </CardTitle>
              {!isEditing ? (
                <PermissionButton
                  permission="clients:edit"
                  whenDenied="hide"
                  size="sm"
                  className="gap-2"
                  onClick={handleStartEditing}
                >
                  <Edit3 className="h-4 w-4" />
                  Edit Profile
                </PermissionButton>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={handleCancelEditing}
                    variant="outline"
                    size="sm"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button size="sm" type="button" onClick={handleSaveClick}>
                    <Save className="h-4 w-4" />
                    Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-6">
              <form
                ref={profileFormRef}
                onSubmit={profileForm.handleSubmit(handleProfileSubmit)}
                className="space-y-8"
              >
                {/* Basic Information Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200">
                  {/* Name Fields */}
                  <div>
                    <Label htmlFor="firstName" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <User className="h-3.5 w-3.5" />
                      First Name *
                    </Label>
                    {isEditing ? (
                      <Input
                        id="firstName"
                        {...profileForm.register("firstName")}
                        className="mt-1"
                      />
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="lastName" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <User className="h-3.5 w-3.5" />
                      Last Name *
                    </Label>
                    {isEditing ? (
                      <Input
                        id="lastName"
                        {...profileForm.register("lastName")}
                        className="mt-1"
                      />
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.lastName}
                      </p>
                    )}
                  </div>
                </div>
                </div>

                {/* Contact Information Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200">
                  <div>
                    <Label htmlFor="personalEmail" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Mail className="h-3.5 w-3.5" />
                      Personal Email
                    </Label>
                    {isEditing ? (
                      <Input
                        id="personalEmail"
                        type="email"
                        {...profileForm.register("personalEmail")}
                        className="mt-1"
                      />
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {(employee as any)?.personalEmail || "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="phone" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Phone className="h-3.5 w-3.5" />
                      Phone Number
                    </Label>
                    {isEditing ? (
                      <Controller
                        control={profileForm.control}
                        name="phone"
                        render={({ field }) => {
                          const handlePhoneChange = (
                            event: ChangeEvent<HTMLInputElement>
                          ) => {
                            const formattedValue = formatPhoneInputValue(
                              event.target.value
                            );

                            field.onChange(formattedValue);
                          };

                          return (
                            <Input
                              id="phone"
                              {...field}
                              value={field.value || ""}
                              className="mt-1"
                              placeholder="(555) 123-4567"
                              onChange={handlePhoneChange}
                            />
                          );
                        }}
                      />
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.phone || "Not provided"}
                      </p>
                    )}
                  </div>
                  </div>
                </div>

                {/* Personal Details Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <IdCard className="h-4 w-4" />
                    Personal Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200">
                  <div>
                    <Label htmlFor="dateOfBirth" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Calendar className="h-3.5 w-3.5" />
                      Date of Birth
                    </Label>
                    {isEditing ? (
                      <Input
                        id="dateOfBirth"
                        type="date"
                        {...profileForm.register("dateOfBirth")}
                        className="mt-1"
                      />
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.dateOfBirth
                          ? formatDateDisplay(employee.dateOfBirth)
                          : "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="sex" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <User className="h-3.5 w-3.5" />
                      Sex
                    </Label>
                    {isEditing ? (
                      <Controller
                        name="sex"
                        control={profileForm.control}
                        render={({ field }) => (
                          <Select
                            value={field.value || ""}
                            onValueChange={(value) => {
                              field.onChange(value);
                              profileForm.setValue("sex", value as ProfileFormValues["sex"], {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true
                              });
                            }}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select sex" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">Male</SelectItem>
                              <SelectItem value="female">Female</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.sex || "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="maritalStatus" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <HeartHandshake className="h-3.5 w-3.5" />
                      Marital Status
                    </Label>
                    {isEditing ? (
                      <Controller
                        name="maritalStatus"
                        control={profileForm.control}
                        render={({ field }) => (
                          <Select
                            value={field.value || ""}
                            onValueChange={(value) => {
                              field.onChange(value);
                              profileForm.setValue("maritalStatus", value as ProfileFormValues["maritalStatus"], {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true
                              });
                            }}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select marital status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="single">Single</SelectItem>
                              <SelectItem value="married">Married</SelectItem>
                              <SelectItem value="divorced">Divorced</SelectItem>
                              <SelectItem value="widowed">Widowed</SelectItem>
                              <SelectItem value="separated">Separated</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.maritalStatus || "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="ssn" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <KeyRound className="h-3.5 w-3.5" />
                      Social Security Number
                    </Label>
                    {isEditing ? (
                      <SsnInput
                        id="ssn"
                        employeeId={employee.id}
                        className="mt-1"
                        value={profileForm.watch("ssn") || ""}
                        onChange={(v) => profileForm.setValue("ssn", v, { shouldDirty: true })}
                        onRevealed={setRevealedSsn}
                      />
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-lg font-medium">
                          {employee.ssn
                            ? showSSN && revealedSsn
                              ? revealedSsn.replace(/\D/g, "").replace(/(\d{3})(\d{2})(\d{4})/, "$1-$2-$3")
                              : "***-**-" + employee.ssn.slice(-4)
                            : "Not provided"}
                        </p>
                        {employee.ssn && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              if (!showSSN && revealedSsn === undefined) {
                                try {
                                  setRevealedSsn((await fetchFullSsns(employee.id, "view")).ssn);
                                } catch {
                                  toast({ title: "Could not load SSN", variant: "destructive" });
                                  return;
                                }
                              }
                              setShowSSN(!showSSN);
                            }}
                            className="h-8 w-8 p-0"
                          >
                            {showSSN ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  </div>
                </div>

                {/* Health Information Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    Health Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200">
                  <div>
                    <Label htmlFor="tobaccoUser" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Smile className="h-3.5 w-3.5" />
                      Tobacco User
                    </Label>
                    {isEditing ? (
                      <Select
                        value={profileForm.watch("tobaccoUser") || ""}
                        onValueChange={(value) =>
                          profileForm.setValue(
                            "tobaccoUser",
                            value as ProfileFormValues["tobaccoUser"]
                          )
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select tobacco use" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.tobaccoUser || "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="disabled" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Shield className="h-3.5 w-3.5" />
                      Disabled
                    </Label>
                    {isEditing ? (
                      <Select
                        value={profileForm.watch("disabled") || ""}
                        onValueChange={(value) =>
                          profileForm.setValue(
                            "disabled",
                            value as ProfileFormValues["disabled"]
                          )
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select disability status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.disabled || "Not provided"}
                      </p>
                    )}
                  </div>
                  </div>
                </div>

                {/* Address Information Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Address Information
                  </h3>
                  <div className="grid grid-cols-1 gap-6 pl-6 border-l-2 border-blue-200">
                <div>
                  <Label className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <MapPin className="h-3.5 w-3.5" />
                    Address
                  </Label>
                  {isEditing ? (
                    <div className="mt-2">
                      <AddressFields control={profileForm.control} />
                    </div>
                  ) : (
                    <div className="mt-2">
                      {(() => {
                        const line = composeAddressLine({
                          address1: employee.address1,
                          address2: employee.address2,
                          city: employee.city,
                          state: employee.state,
                          zipCode: employee.zipCode,
                        });
                        const parts = [
                          line,
                          employee.county || "",
                          employee.country || "",
                        ].filter(
                          (v) => typeof v === "string" && v.trim().length > 0
                        );
                        return parts.length > 0 ? (
                          <div className="space-y-1">
                            {parts.map((p) => (
                              <p key={p} className="text-lg font-medium">
                                {p}
                              </p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-base font-medium text-gray-900 dark:text-gray-100">Not provided</p>
                        );
                      })()}
                    </div>
                  )}
                </div>
                  </div>
                </div>

                {isEditing && (
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">Save Changes</Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Emergency Contacts Section */}
          <Card className="border-l-4 border-l-orange-500 mt-6">
            <CardHeader className="bg-orange-50 dark:bg-orange-950/20 flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-600" />
                Emergency Contacts ({emergencyContacts.length})
              </CardTitle>
              <PermissionButton
                permission="clients:edit"
                whenDenied="hide"
                size="sm"
                className="gap-2"
                onClick={() => {
                  setEditingEmergencyContact(null);
                  setShowAddEmergencyContact(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Add Contact
              </PermissionButton>
              <EmergencyContactModal
                open={showAddEmergencyContact}
                onOpenChange={(open) => {
                  setShowAddEmergencyContact(open);
                  if (!open) {
                    setEditingEmergencyContact(null);
                  }
                }}
                onSubmit={handleEmergencyContactSubmit}
                editingContact={editingEmergencyContact}
                employeeName={employee ? `${employee.firstName} ${employee.lastName}` : undefined}
                employeeAddress={employee ? {
                  address1: employee.address1 || "",
                  address2: employee.address2 || "",
                  city: employee.city || "",
                  state: employee.state || "",
                  zipCode: employee.zipCode || "",
                  county: employee.county || "",
                  country: employee.country || "United States",
                } : undefined}
                isPending={
                  createEmergencyContactMutation.isPending ||
                  updateEmergencyContactMutation.isPending
                }
              />
            </CardHeader>
            <CardContent>
              {emergencyContacts.length > 0 ? (
                <div className="space-y-4">
                  {emergencyContacts.map((contact: EmergencyContact) => (
                    <div
                      key={contact.id}
                      className="border rounded-lg p-4 hover:bg-muted/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h4 className="font-medium">{contact.name}</h4>
                            {contact.relationship && (
                              <Badge variant="outline" className="capitalize">
                                {contact.relationship}
                              </Badge>
                            )}
                            {contact.contactType && (
                              <Badge 
                                variant={contact.contactType === "Primary" ? "default" : "secondary"}
                                className="capitalize"
                              >
                                {contact.contactType}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Phone</p>
                              <p>{contact.phone || "Not provided"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Email</p>
                              <p>{contact.email || "Not provided"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Address</p>
                              <p>{contact.address || "Not provided"}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <PermissionButton
                            permission="clients:edit"
                            whenDenied="hide"
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditEmergencyContact(contact)}
                          >
                            <Edit className="h-4 w-4" />
                          </PermissionButton>
                          <PermissionButton
                            permission="clients:edit"
                            whenDenied="hide"
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handleDeleteEmergencyContact(contact.id)
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </PermissionButton>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">
                    No Emergency Contacts
                  </h3>
                  <p className="text-sm">
                    Add emergency contact information for this employee.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* TODO: Portal Access Section - EmployeeAdminAccess component not yet created */}
        </TabsContent>

        {/* Employment Tab */}
        <TabsContent value="employment">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="bg-green-50 dark:bg-green-950/20 flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-green-600" />
                Employment Information
              </CardTitle>
              {!isEditing ? (
                <PermissionButton
                  permission="clients:edit"
                  whenDenied="hide"
                  size="sm"
                  className="gap-2"
                  onClick={handleStartEditing}
                >
                  <Edit3 className="h-4 w-4" />
                  Edit Employment
                </PermissionButton>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={handleCancelEditing}
                    variant="outline"
                    size="sm"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button size="sm" type="button" onClick={handleSaveClick}>
                    <Save className="h-4 w-4" />
                    Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-4">
              <form
                ref={employmentFormRef}
                onSubmit={employmentForm.handleSubmit(handleEmploymentSubmit, handleEmploymentInvalid)}
                className="space-y-8"
              >
                {/* Work Contact Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Work Contact
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200">
                    <div>
                      <Label htmlFor="workEmail" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Mail className="h-3.5 w-3.5" />
                        Work Email
                      </Label>
                      {isEditing ? (
                        <Input
                          id="workEmail"
                          type="email"
                          className="mt-1"
                          placeholder="john.doe@company.com"
                          {...employmentForm.register("email")}
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.email || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="workPhone" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Phone className="h-3.5 w-3.5" />
                        Work Phone
                      </Label>
                      {isEditing ? (
                        <Controller
                          control={employmentForm.control}
                          name="workPhone"
                          render={({ field }) => {
                            const handleWorkPhoneChange = (
                              event: ChangeEvent<HTMLInputElement>
                            ) => {
                              const formattedValue = formatPhoneInputValue(
                                event.target.value
                              );
                              field.onChange(formattedValue);
                            };

                            return (
                              <Input
                                id="workPhone"
                                {...field}
                                value={field.value || ""}
                                className="mt-1"
                                placeholder="(555) 123-4567"
                                onChange={handleWorkPhoneChange}
                              />
                            );
                          }}
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {(employee as any)?.workPhone || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Job Information Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Job Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200">
                  <div>
                    <Label htmlFor="position" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Briefcase className="h-3.5 w-3.5" />
                      Position *
                    </Label>
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Select
                          value={
                            employmentForm.watch("jobTitleId")?.toString() || ""
                          }
                          onValueChange={(value) =>
                            employmentForm.setValue(
                              "jobTitleId",
                              parseInt(value)
                            )
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue
                              placeholder={
                                employee.position || "Select job title"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {(orgJobTitles || []).map((jt: any) => (
                              <SelectItem key={jt.id} value={String(jt.id)}>
                                {jt.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Dialog
                          open={isAddJobTitleOpen}
                          onOpenChange={setIsAddJobTitleOpen}
                        >
                          <DialogTrigger asChild>
                            <Button type="button" variant="outline">
                              Add
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Add Job Title</DialogTitle>
                            </DialogHeader>
                            <div className="flex gap-2 items-end">
                              <div className="flex-1">
                                <Label>Name</Label>
                                <Input
                                  value={newJobTitle}
                                  onChange={(e) =>
                                    setNewJobTitle(e.target.value)
                                  }
                                />
                              </div>
                              <Button
                                type="button"
                                onClick={() =>
                                  newJobTitle && addJobTitle.mutate(newJobTitle)
                                }
                              >
                                Save
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.position}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="department" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Building2 className="h-3.5 w-3.5" />
                      Department
                    </Label>
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Select
                          value={
                            employmentForm.watch("departmentId")?.toString() ||
                            ""
                          }
                          onValueChange={(value) =>
                            employmentForm.setValue(
                              "departmentId",
                              parseInt(value)
                            )
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue
                              placeholder={
                                employee.department || "Select department"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {(orgDepartments || []).map((d: any) => (
                              <SelectItem key={d.id} value={String(d.id)}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Dialog
                          open={isAddDepartmentOpen}
                          onOpenChange={setIsAddDepartmentOpen}
                        >
                          <DialogTrigger asChild>
                            <Button type="button" variant="outline">
                              Add
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Add Department</DialogTitle>
                            </DialogHeader>
                            <div className="flex gap-2 items-end">
                              <div className="flex-1">
                                <Label>Name</Label>
                                <Input
                                  value={newDepartment}
                                  onChange={(e) =>
                                    setNewDepartment(e.target.value)
                                  }
                                />
                              </div>
                              <Button
                                type="button"
                                onClick={() =>
                                  newDepartment &&
                                  addDepartment.mutate(newDepartment)
                                }
                              >
                                Save
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.department}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="manager" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Users className="h-3.5 w-3.5" />
                      Manager
                    </Label>
                    {isEditing ? (
                      <Select
                        value={employmentForm.watch("managerEmployeeId")?.toString() || "__none__"}
                        onValueChange={(value) =>
                          employmentForm.setValue(
                            "managerEmployeeId",
                            value && value !== "__none__" ? parseInt(value) : null,
                            { shouldDirty: true }
                          )
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select manager" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No manager</SelectItem>
                          {potentialManagers.map((mgr: Employee) => (
                            <SelectItem key={mgr.id} value={String(mgr.id)}>
                              {mgr.firstName} {mgr.lastName}
                              {mgr.position ? ` - ${mgr.position}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {(() => {
                          const manager = potentialManagers.find(
                            (m: Employee) => m.id === (employee as any)?.managerEmployeeId
                          );
                          return manager
                            ? `${manager.firstName} ${manager.lastName}`
                            : "Not assigned";
                        })()}
                      </p>
                    )}
                  </div>
                  </div>
                </div>

                {/* Work Details Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Work Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200">
                  <div>
                    <Label htmlFor="employmentType" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Clock className="h-3.5 w-3.5" />
                      Employment Type
                    </Label>
                    {isEditing ? (
                      <>
                        <Select
                          value={employmentForm.watch("employmentType") || ""}
                          onValueChange={(value) =>
                            employmentForm.setValue(
                              "employmentType",
                              value as EmploymentFormValues["employmentType"],
                              { shouldDirty: true }
                            )
                          }
                        >
                          <SelectTrigger
                            className={`mt-1 ${employmentForm.formState.errors.employmentType ? "border-destructive" : ""}`}
                          >
                            <SelectValue placeholder="Select employment type" />
                          </SelectTrigger>
                          <SelectContent>
                            {EMPLOYMENT_TYPES.map((t) => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {employmentForm.formState.errors.employmentType && (
                          <p className="mt-1 text-sm text-destructive">
                            {employmentForm.formState.errors.employmentType.message as string || "Employment Type is required"}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.employmentType || "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="location" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <MapPin className="h-3.5 w-3.5" />
                      Location
                    </Label>
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Select
                          value={
                            employmentForm.watch("locationId")?.toString() || ""
                          }
                          onValueChange={(value) =>
                            employmentForm.setValue(
                              "locationId",
                              parseInt(value)
                            )
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            {(orgLocations || []).map((l: any) => (
                              <SelectItem key={l.id} value={String(l.id)}>
                                {[l.officeName, l.city, l.state]
                                  .filter(Boolean)
                                  .join(" – ")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Dialog
                          open={isAddLocationOpen}
                          onOpenChange={setIsAddLocationOpen}
                        >
                          <DialogTrigger asChild>
                            <Button type="button" variant="outline">
                              Add
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Add Location</DialogTitle>
                            </DialogHeader>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <Label>Office</Label>
                                <Input
                                  value={newLocOffice}
                                  onChange={(e) =>
                                    setNewLocOffice(e.target.value)
                                  }
                                />
                              </div>
                              <div>
                                <Label>City</Label>
                                <Input
                                  value={newLocCity}
                                  onChange={(e) =>
                                    setNewLocCity(e.target.value)
                                  }
                                />
                              </div>
                              <div>
                                <Label>State</Label>
                                <Input
                                  value={newLocState}
                                  onChange={(e) =>
                                    setNewLocState(e.target.value)
                                  }
                                />
                              </div>
                            </div>
                            <div className="mt-2">
                              <Button
                                type="button"
                                onClick={() =>
                                  addLocation.mutate({
                                    officeName: newLocOffice || undefined,
                                    city: newLocCity || undefined,
                                    state: newLocState || undefined,
                                  })
                                }
                              >
                                Save
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {(() => {
                          const loc = (orgLocations || []).find(
                            (l: any) => l.id === (employee as any)?.locationId
                          );
                          if (loc) {
                            return [loc.officeName, loc.city, loc.state]
                              .filter(Boolean)
                              .join(" – ");
                          }
                          // No org location assigned — surface the employee's
                          // own city/state, explicitly labeled as coming from
                          // their address rather than an assigned location.
                          const addressLoc = [employee.city, employee.state]
                            .filter(Boolean)
                            .join(", ");
                          if (!addressLoc) return "Not provided";
                          return (
                            <>
                              {addressLoc}{" "}
                              <span className="text-sm font-normal text-gray-500">
                                (from address — no work location assigned)
                              </span>
                            </>
                          );
                        })()}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="employeeClass" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Users className="h-3.5 w-3.5" />
                      Employee Class
                    </Label>
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Select
                          value={
                            employmentForm
                              .watch("employeeClassId")
                              ?.toString() || ""
                          }
                          onValueChange={(value) =>
                            employmentForm.setValue(
                              "employeeClassId",
                              parseInt(value)
                            )
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select employee class" />
                          </SelectTrigger>
                          <SelectContent>
                            {(orgEmployeeClasses || []).map((c: any) => (
                              <SelectItem key={c.id} value={String(c.id)}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Dialog
                          open={isAddClassOpen}
                          onOpenChange={setIsAddClassOpen}
                        >
                          <DialogTrigger asChild>
                            <Button type="button" variant="outline">
                              Add
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Add Employee Class</DialogTitle>
                            </DialogHeader>
                            <div className="flex gap-2 items-end">
                              <div className="flex-1">
                                <Label>Name</Label>
                                <Input
                                  value={newClass}
                                  onChange={(e) => setNewClass(e.target.value)}
                                />
                              </div>
                              <Button
                                type="button"
                                onClick={() =>
                                  newClass && addEmployeeClass.mutate(newClass)
                                }
                              >
                                Save
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {(orgEmployeeClasses || []).find(
                          (c: any) =>
                            c.id === (employee as any)?.employeeClassId
                        )?.name || "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="division" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Building2 className="h-3.5 w-3.5" />
                      Division
                    </Label>
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Select
                          value={
                            employmentForm.watch("divisionId")?.toString() || ""
                          }
                          onValueChange={(value) =>
                            employmentForm.setValue(
                              "divisionId",
                              parseInt(value)
                            )
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select division" />
                          </SelectTrigger>
                          <SelectContent>
                            {(orgDivisions || []).map((d: any) => (
                              <SelectItem key={d.id} value={String(d.id)}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Dialog
                          open={isAddDivisionOpen}
                          onOpenChange={setIsAddDivisionOpen}
                        >
                          <DialogTrigger asChild>
                            <Button type="button" variant="outline">
                              Add
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Add Division</DialogTitle>
                            </DialogHeader>
                            <div className="flex gap-2 items-end">
                              <div className="flex-1">
                                <Label>Name</Label>
                                <Input
                                  value={newDivision}
                                  onChange={(e) =>
                                    setNewDivision(e.target.value)
                                  }
                                />
                              </div>
                              <Button
                                type="button"
                                disabled={!newDivision.trim()}
                                onClick={() =>
                                  newDivision.trim() &&
                                  addDivision.mutate(newDivision.trim())
                                }
                              >
                                Save
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {(orgDivisions || []).find(
                          (d: any) => d.id === (employee as any)?.divisionId
                        )?.name || "Not provided"}
                      </p>
                    )}
                  </div>
                  </div>
                </div>

                {/* Schedule & Compensation Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Schedule & Compensation
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200">
                  <div>
                    <Label htmlFor="scheduledHours" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Clock className="h-3.5 w-3.5" />
                      Scheduled Hours / Week
                    </Label>
                    {isEditing ? (
                      <Input
                        id="scheduledHours"
                        {...employmentForm.register("scheduledHours")}
                        className="mt-1"
                        placeholder="e.g. 40"
                      />
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.scheduledHours || "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="compensationType" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <CreditCard className="h-3.5 w-3.5" />
                      Compensation Type
                    </Label>
                    {isEditing ? (
                      <Select
                        value={employmentForm.watch("compensationType") || ""}
                        onValueChange={(value) =>
                          employmentForm.setValue(
                            "compensationType",
                            value as EmploymentFormValues["compensationType"]
                          )
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select compensation type" />
                        </SelectTrigger>
                        <SelectContent>
                          {COMPENSATION_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.compensationType || "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="compensationAmount" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <DollarSign className="h-3.5 w-3.5" />
                      Compensation Amount
                    </Label>
                    {isEditing ? (
                      <>
                        <Input
                          id="compensationAmount"
                          {...employmentForm.register("compensationAmount")}
                          className="mt-1"
                        />
                        <Label htmlFor="compensationEffectiveDate" className="mt-3 block text-xs text-gray-600 dark:text-gray-400">
                          Effective date of this amount
                        </Label>
                        <Input
                          id="compensationEffectiveDate"
                          type="date"
                          {...employmentForm.register("compensationEffectiveDate")}
                          className="mt-1"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          Leave blank for today; set a date to record when the raise took effect.
                        </p>
                      </>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.compensationAmount
                          ? new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(Number(employee.compensationAmount))
                          : "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="payCycle" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Calendar className="h-3.5 w-3.5" />
                      Pay Cycle
                    </Label>
                    {isEditing ? (
                      <Select
                        value={employmentForm.watch("payCycle") || ""}
                        onValueChange={(value) =>
                          employmentForm.setValue(
                            "payCycle",
                            value as EmploymentFormValues["payCycle"]
                          )
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select pay cycle" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAY_CYCLES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.payCycle || "Not provided"}
                      </p>
                    )}
                  </div>
                  </div>
                  <div className="pl-6">
                    <CompensationHistoryCard
                      employeeId={employee.id}
                      canEdit={canAny(["clients:create", "prospects:create", "clients:edit"])}
                    />
                  </div>
                </div>

                {/* Dates & Identification Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Dates & Identification
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200">
                  <div>
                    <Label htmlFor="hireDate" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Calendar className="h-3.5 w-3.5" />
                      Hire Date
                    </Label>
                    {isEditing ? (
                      (employee as any).hireDateLocked ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-block w-full">
                                <Input
                                  id="hireDate"
                                  type="date"
                                  {...employmentForm.register("hireDate")}
                                  className="mt-1"
                                  disabled
                                />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>
                                Hire date is locked because this employee has existing benefit
                                enrollments. Editing it would desynchronize coverage effective
                                dates, premiums, and ACA reporting. Contact support if a
                                correction is required.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Input
                          id="hireDate"
                          type="date"
                          {...employmentForm.register("hireDate")}
                          className="mt-1"
                        />
                      )
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.hireDate
                          ? formatDateDisplay(employee.hireDate)
                          : "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="employeeId" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <IdCard className="h-3.5 w-3.5" />
                      Employee ID
                    </Label>
                    {isEditing ? (
                      <Input
                        id="employeeId"
                        {...employmentForm.register("employeeId")}
                        className="mt-1"
                      />
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {employee.employeeId || "Not assigned"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="status" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Briefcase className="h-3.5 w-3.5" />
                      Employment Status
                    </Label>
                    {isEditing ? (
                      <Select
                        value={employmentForm.watch("status") || ""}
                        onValueChange={(value) =>
                          employmentForm.setValue(
                            "status",
                            value as EmploymentFormValues["status"]
                          )
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {EMPLOYEE_STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                        {formatStatus(employee.status) || "Not provided"}
                      </p>
                    )}
                  </div>

                  {/* Termination Details - only shown when employee is terminated */}
                  {employee.status === "terminated" && (
                    <>
                      <div>
                        <Label className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                          <Calendar className="h-3.5 w-3.5" />
                          Termination Date
                        </Label>
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.terminationDate 
                            ? formatDateDisplay(employee.terminationDate) 
                            : "Not provided"}
                        </p>
                      </div>

                      <div>
                        <Label className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                          <AlertCircle className="h-3.5 w-3.5" />
                          Termination Reason
                        </Label>
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.terminationReason 
                            ? employee.terminationReason.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
                            : "Not provided"}
                        </p>
                      </div>

                      {employee.terminationNotes && (
                        <div className="col-span-2">
                          <Label className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                            <FileText className="h-3.5 w-3.5" />
                            Termination Notes
                          </Label>
                          <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                            {employee.terminationNotes}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Agent License Number - only shown for agency employees */}
                  {employee.agencyId && !employee.clientId && !employee.prospectId && (
                    <div>
                      <Label htmlFor="licenseNumber" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <IdCard className="h-3.5 w-3.5" />
                        Agent License Number
                      </Label>
                      {isEditing ? (
                        <Input
                          id="licenseNumber"
                          {...employmentForm.register("licenseNumber")}
                          className="mt-1"
                          placeholder="e.g. 0A12345"
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {(employee as any)?.licenseNumber || "Not provided"}
                        </p>
                      )}
                    </div>
                  )}
                  </div>
                </div>

                {isEditing && (
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">Save Changes</Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Benefits Tab */}
        <TabsContent value="benefits">
          <div className="space-y-6">
            {/* Date of Birth Required Banner */}
            {employee && !employee.dateOfBirth && (
              <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                    <AlertTriangle className="w-5 h-5" />
                    Date of Birth Required
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-700 dark:text-amber-300 mb-4">
                    This employee's date of birth is required to calculate benefits premiums and determine eligibility.
                    Please update their profile before configuring benefits.
                  </p>
                  <Button
                    variant="outline"
                    className="border-amber-600 text-amber-700 hover:bg-amber-100"
                    onClick={() => setActiveTab("profile")}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Update Profile
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Configure Benefits — only visible when at least one type is undecided. */}
            {(() => {
              const totalTypes = offeredTypeIds.size;
              const configuredTypes = Math.max(0, totalTypes - typeSections.remainingCount);
              return (
                <>
                  {/* Coverage banner — Configure Benefits CTA lives inside */}
                  <CoverageHeaderBanner
                    totalTypes={totalTypes}
                    configuredTypes={configuredTypes}
                    segments={typeSections.segmentCounts}
                    totalMonthly={totalMonthly}
                    payCycle={employee?.payCycle}
                    isLoading={isLoadingEmployeeBenefits}
                    unpricedPlans={unpricedPlans}
                    configureCta={{
                      remainingCount: typeSections.remainingCount,
                      onClick: () => openConfigureBenefits(null),
                    }}
                  />

                  {/* Pending enrollment request (QLE/off-cycle) awaiting broker approval */}
                  {pendingEnrollmentTask && (
                    <PendingEnrollmentRequestCard
                      elections={
                        Array.isArray((pendingEnrollmentTask as any)?.customFields?.requestedEnrollments)
                          ? (pendingEnrollmentTask as any).customFields.requestedEnrollments
                          : []
                      }
                      waivers={
                        Array.isArray((pendingEnrollmentTask as any)?.customFields?.requestedWaivers)
                          ? (pendingEnrollmentTask as any).customFields.requestedWaivers
                          : []
                      }
                      effectiveDate={(pendingEnrollmentTask as any)?.customFields?.effectiveDate}
                      plans={clientPlans as any[]}
                      reviewHref={`/tasks?open=${(pendingEnrollmentTask as any).id}`}
                      description={`${employee?.firstName ?? "This employee"}'s benefit elections are pending your approval.`}
                    />
                  )}

                  {/* Upcoming Benefits (kept as a top-level section per product decision) */}
                  {pendingBenefits.length > 0 && (
                    <Card className="border-l-4 border-l-blue-500">
                      <CardHeader className="bg-blue-50 dark:bg-blue-950/20">
                        <CardTitle className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-blue-600" />
                          Upcoming Benefits
                          <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">
                            {pendingBenefits.length}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6">
                        <div className="space-y-3">
                          {(pendingBenefits as any[]).map((benefit: any) => (
                            <ActiveBenefitRow
                              key={benefit.id}
                              benefit={benefit}
                              employee={employee}
                              dependents={dependents as any}
                              employeeBenefits={employeeBenefits as any}
                              onModify={() => openModifyForType(benefit?.plan?.type || "")}
                            />
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Type-sectioned elections */}
                  <div className="space-y-5">
                    {typeSections.sections
                      .filter((s) => s.activeRows.length > 0 || s.waivedRows.length > 0 || s.emptyTypes.length > 0)
                      .map((section) => {
                        const hasAnyRow = section.activeRows.length > 0 || section.waivedRows.length > 0;
                        const defaultOpen = !section.def.collapsible || hasAnyRow;
                        return (
                          <BenefitSection
                            key={section.def.id}
                            def={section.def}
                            counts={{
                              enrolled: section.activeRows.length,
                              pending: section.pendingCount,
                              waived: section.waivedRows.length,
                              unconfigured: section.emptyTypes.length,
                            }}
                            defaultOpen={defaultOpen}
                            onConfigureSection={
                              section.emptyTypes.length > 0
                                ? () => openConfigureBenefits(section.def.types as unknown as string[])
                                : undefined
                            }
                          >
                            {section.activeRows.map((b: any) => (
                              <ActiveBenefitRow
                                key={b.id}
                                benefit={b}
                                employee={employee}
                                dependents={dependents as any}
                                employeeBenefits={employeeBenefits as any}
                                onModify={() => openModifyForType(b?.plan?.type || "")}
                              />
                            ))}
                            {section.waivedRows.map((b: any) => {
                              const pid = Number(b?.planId);
                              const needsConfig =
                                resolveNeedsConfigReason({
                                  loaded: planContributionsFetched && Number.isFinite(pid),
                                  isFsa: isFsaPlanType(String(b?.plan?.type || "")),
                                  isLifeDisability: isLifeDisabilityType(String(b?.plan?.type || "")),
                                  contributionCount: (planContributions as any[]).filter(
                                    (c: any) => c.planId === pid
                                  ).length,
                                  // no premium recompute on waived rows → coverage unknown
                                  coverageAmountResolved: null,
                                }) !== null;
                              return (
                                <WaivedBenefitRow
                                  key={b.id}
                                  benefit={b}
                                  needsConfig={needsConfig}
                                  onEditWaiver={() => openModifyForType(b?.plan?.type || "")}
                                  onEnrollInstead={() => openModifyForType(b?.plan?.type || "")}
                                />
                              );
                            })}
                            {section.emptyTypes.map((t) => {
                              const typePlans = plansForType(t);
                              return (
                                <EmptyTypeRow
                                  key={`empty-${section.def.id}-${t}`}
                                  planType={t}
                                  availablePlanCount={typePlans.filter(isClientPlanEligible).length}
                                  ineligibleReason={typeIneligibleReason(typePlans)}
                                  onConfigure={() => openConfigureBenefits([t])}
                                />
                              );
                            })}
                          </BenefitSection>
                        );
                      })}
                  </div>

                  {/* Cost Summary */}
                  <Card className="border-l-4 border-l-green-500">
                    <CardHeader className="bg-green-50 dark:bg-green-950/20">
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-green-600" />
                        {hasNonMonthlyPayCycle(employee?.payCycle)
                          ? `${getPayCycleLabelCapitalized(employee?.payCycle)} Deduction Summary`
                          : "Monthly Cost Summary"}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        Current {getPayCycleLabel(employee?.payCycle)} deductions from payroll for active benefit enrollments.
                      </p>
                    </CardHeader>
                    <CardContent className="p-6">
                      <DeductionSummaryContent
                        items={deductionItems}
                        totalMonthly={totalMonthly}
                        payCycle={employee?.payCycle}
                        isLoading={isLoadingEmployeeBenefits}
                      />
                    </CardContent>
                  </Card>

                  {/* Configure-Benefits wizard dialog (scoped or global). */}
                  <Dialog
                    open={showConfigureBenefits}
                    onOpenChange={(o) => {
                      setShowConfigureBenefits(o);
                      if (!o) setConfigureScope(null);
                    }}
                  >
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>
                          Configure Benefits for {employee?.firstName} {employee?.lastName}
                        </DialogTitle>
                        <DialogDescription>
                          Select and configure benefit plans for this employee. Add plans not currently enrolled or waived.
                        </DialogDescription>
                      </DialogHeader>
                      <BenefitsConfigurationForm
                        employee={employee}
                        dependents={dependents}
                        // Group-aware enrollmentRequired feeds the wizard's mandatory-plan lock.
                        availablePlans={clientPlansWithEligibility.filter(isClientPlanEligible)}
                        existingEnrollments={baseEnrollments}
                        isLoading={isLoadingClientPlans}
                        enrollmentSource="broker_manual"
                        restrictToTypes={configureScope ?? undefined}
                        onComplete={() => {
                          queryClient.invalidateQueries({
                            queryKey: ["/api/employees", employeeId, "benefits"],
                          });
                          setShowConfigureBenefits(false);
                          setConfigureScope(null);
                        }}
                      />
                    </DialogContent>
                  </Dialog>

                  {/* Unified per-type Modify modal */}
                  {modifyContext && (
                    <ModifyTypeModal
                      open={!!modifyContext}
                      onClose={() => setModifyContext(null)}
                      employee={employee}
                      dependents={dependents as any}
                      employeeBenefits={employeeBenefits as any}
                      type={modifyContext.type}
                      currentEnrollment={modifyContext.current}
                      waivedEnrollment={modifyContext.waived}
                      availablePlans={plansForType(modifyContext.type)}
                      onSaved={() => {
                        queryClient.invalidateQueries({
                          queryKey: ["/api/employees", employeeId, "benefits"],
                        });
                      }}
                    />
                  )}
                </>
              );
            })()}
          </div>
        </TabsContent>

        {/* Dependents Tab */}
        <TabsContent value="dependents">
          <DependentsList
            employeeId={parseInt(employeeId || "0")}
            employeeName={`${employee.firstName} ${employee.lastName}`}
            onNavigateToEnrollments={() => setActiveTab("benefits")}
          />
        </TabsContent>

        {/* ACA Tab: read from the ACA engine snapshot */}
        <TabsContent value="aca">
          <EmployeeAcaSummary clientId={employee?.clientId} employeeId={employeeId} />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader className="bg-indigo-50 dark:bg-indigo-950/20">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-indigo-600" />
                History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Tabs defaultValue="changes" className="space-y-6">
                <TabsList className="grid w-full grid-cols-2 bg-[#eff6fe]">
                  <TabsTrigger value="changes" className="bg-[#eff6fe] data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white">Changes</TabsTrigger>
                  <TabsTrigger value="contact" className="bg-[#eff6fe] data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white">Contact History</TabsTrigger>
                </TabsList>

                {/* Changes Tab */}
                <TabsContent value="changes" className="space-y-4">
                  {Array.isArray(historyLogs) && historyLogs.length > 0 ? (
                    <div className="space-y-4">
                      {redactSsnDeep([...historyLogs]).reverse().map((log: any) => (
                        <div key={log.id} className="border rounded-lg p-4">
                          {log.description && (
                            <div className="text-sm font-medium mb-2">
                              {log.description}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mb-3">
                            {new Date(log.timestamp).toLocaleString()}
                            {log.changedBy && (
                              <span className="ml-2">
                                · Changed by: <span className="font-medium">{log.changedBy}</span>
                              </span>
                            )}
                          </div>
                          {log.changes && (
                            <div className="text-sm">
                              <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 text-xs space-y-1">
                                {(() => {
                                  try {
                                    const changes =
                                      typeof log.changes === "string"
                                        ? JSON.parse(log.changes)
                                        : log.changes;
                                    return Object.entries(changes).map(
                                      ([field, change]: [string, any]) => (
                                        <div key={field} className="flex items-center gap-2">
                                          <span className="font-medium capitalize">
                                            {field}:
                                          </span>
                                          {change.from && (
                                            <span className="text-red-600 dark:text-red-400">
                                              {change.from}
                                            </span>
                                          )}
                                          {change.from && change.to && (
                                            <span className="text-gray-400">→</span>
                                          )}
                                          <span className="text-green-600 dark:text-green-400">
                                            {change.to}
                                          </span>
                                        </div>
                                      )
                                    );
                                  } catch {
                                    return <div>{log.changes}</div>;
                                  }
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No history available</p>
                      <p className="text-sm">
                        Employee changes and activities will appear here.
                      </p>
                    </div>
                  )}
                </TabsContent>

                {/* Contact History Tab */}
                <TabsContent value="contact" className="space-y-4">
                  <div className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <h3 className="text-sm font-medium">Contact Interactions ({contactHistory.length})</h3>
              <Dialog
                open={showAddContactHistory}
                onOpenChange={setShowAddContactHistory}
              >
                <DialogTrigger asChild>
                  <PermissionButton
                    permission="clients:edit"
                    whenDenied="hide"
                    size="sm"
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Interaction
                  </PermissionButton>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Add Contact Interaction</DialogTitle>
                  </DialogHeader>
                  <form
                    onSubmit={contactHistoryForm.handleSubmit(
                      handleAddContactHistory
                    )}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ci_type">Contact Type</Label>
                        <Select
                          value={contactHistoryForm.watch("type") as any}
                          onValueChange={(v) =>
                            contactHistoryForm.setValue("type" as any, v, { shouldValidate: true })
                          }
                        >
                          <SelectTrigger id="ci_type">
                            <SelectValue placeholder="Select contact type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="call">Phone Call</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="meeting">Meeting</SelectItem>
                                  <SelectItem value="text">Text Message</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        {contactHistoryForm.formState.errors.type && (
                          <p className="text-sm text-destructive mt-1">{String(contactHistoryForm.formState.errors.type.message)}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="ci_outcome">Outcome</Label>
                        <Select
                          value={contactHistoryForm.watch("outcome") as any}
                          onValueChange={(v) =>
                            contactHistoryForm.setValue("outcome" as any, v)
                          }
                        >
                          <SelectTrigger id="ci_outcome">
                            <SelectValue placeholder="Select outcome" />
                          </SelectTrigger>
                          <SelectContent>
                                  {Object.entries(OUTCOME_LABELS).map(
                                    ([key, label]) => (
                                      <SelectItem key={key} value={key}>
                                        {label}
                            </SelectItem>
                                    )
                                  )}
                          </SelectContent>
                        </Select>
                            </div>
                      </div>
                      <div>
                            <Label htmlFor="ci_subject">Subject</Label>
                        <Input
                              id="ci_subject"
                              {...contactHistoryForm.register("subject")}
                              placeholder="What was the interaction about?"
                        />
                        {contactHistoryForm.formState.errors.subject && (
                          <p className="text-sm text-destructive mt-1">{String(contactHistoryForm.formState.errors.subject.message)}</p>
                        )}
                      </div>
                          <div>
                            <Label htmlFor="ci_date">Contact Date & Time</Label>
                            <Input
                              id="ci_date"
                              type="datetime-local"
                              {...contactHistoryForm.register("contactDate")}
                            />
                    </div>
                          <div>
                            <Label htmlFor="ci_notes">Notes</Label>
                            <Textarea
                              id="ci_notes"
                              {...contactHistoryForm.register("notes")}
                              placeholder="Additional notes about this interaction"
                              rows={4}
                            />
                          </div>
                          <div>
                            <Label htmlFor="ci_followup">Next Follow-up Date</Label>
                            <Input
                              id="ci_followup"
                              type="datetime-local"
                              {...contactHistoryForm.register("nextFollowUp")}
                            />
                          </div>
                          <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowAddContactHistory(false)}
                      >
                        Cancel
                      </Button>
                            <Button type="submit">Add Interaction</Button>
                          </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
                  </div>

              {contactHistory.length > 0 ? (
                    <div className="space-y-4">
                      {contactHistory.map((interaction: any) => (
                        <div
                          key={interaction.id}
                          className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                <div>
                              <h4 className="font-medium text-sm">
                                {interaction.subject || "No subject"}
                              </h4>
                              <p className="text-xs text-muted-foreground">
                                {interaction.type
                                  ? interaction.type.charAt(0).toUpperCase() +
                                    interaction.type.slice(1)
                                  : "Contact"}{" "}
                                {interaction.contactDate &&
                                  new Date(
                                    interaction.contactDate
                                  ).toLocaleString()}
                              </p>
                  </div>
                            {interaction.outcome && (
                              <Badge variant="outline">
                                {OUTCOME_LABELS[interaction.outcome] ||
                                  interaction.outcome}
                            </Badge>
                            )}
                              </div>
                              {interaction.notes && (
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                                  {interaction.notes}
                            </p>
                          )}
                          {interaction.nextFollowUp && (
                            <p className="text-xs text-muted-foreground">
                              Follow-up:{" "}
                              {new Date(
                                interaction.nextFollowUp
                              ).toLocaleString()}
                            </p>
                              )}
                            </div>
                      ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No contact history</p>
                      <p className="text-sm">
                        Contact interactions will appear here.
                  </p>
                </div>
              )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <DocumentsTabContent employeeId={employeeId || "0"} clientId={employee?.clientId ?? undefined} employeeName={employee?.firstName && employee?.lastName ? `${employee.firstName} ${employee.lastName}` : "Employee"} />

        {/* Edit Enrollment Dialog */}
        <Dialog open={showEditEnrollment} onOpenChange={setShowEditEnrollment}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modify Benefit Enrollment</DialogTitle>
            </DialogHeader>
            {editingEnrollment && (
              <div className="space-y-4">
                {/* Waive coverage toggle */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      id="edit_waived"
                      type="checkbox"
                      checked={editWaived}
                      onChange={(e) => setEditWaived(e.target.checked)}
                    />
                    <Label htmlFor="edit_waived">Waive Coverage</Label>
                  </div>
                  {editWaived && (
                    <div className="space-y-4">
                    <div>
                        <Label htmlFor="edit-effective-date">Waived Coverage Start Date *</Label>
                      <Input
                          id="edit-effective-date"
                          type="date"
                          value={editEffectiveDate}
                          onChange={(e) => setEditEffectiveDate(e.target.value)}
                        className="mt-1"
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          Select when the waived coverage should begin (must be after plan termination date)
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="edit-event-type">Reason for Waiving *</Label>
                        <Select value={editEventType} onValueChange={setEditEventType}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select reason" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="voluntary_waiver">Voluntary Waiver</SelectItem>
                            <SelectItem value="switched_to_spouse_plan">Switched to Spouse's Plan</SelectItem>
                            <SelectItem value="qualifying_life_event">Qualifying Life Event</SelectItem>
                            <SelectItem value="cobra_conversion">COBRA Conversion</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="edit-event-description">Additional Details (Optional)</Label>
                        <textarea
                          id="edit-event-description"
                          value={editEventDescription}
                          onChange={(e) => setEditEventDescription(e.target.value)}
                          placeholder="Provide additional context about this change..."
                          maxLength={500}
                          rows={3}
                          className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          {editEventDescription.length}/500 characters
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Dependents selector for this enrollment */}
                <div>
                  <Label>Dependents</Label>
                  <div className="border rounded p-2 max-h-48 overflow-y-auto mt-1">
                    {(dependents || []).length === 0 ? (
                      <div className="text-sm text-muted-foreground">None</div>
                    ) : (
                      (dependents || []).map((d: any) => {
                        const id = d.id as number;
                        const checked = (
                          editSelectedDependentIds || []
                        ).includes(id);
                        return (
                          <label
                            key={id}
                            className="flex items-center justify-between gap-2 py-1 text-sm"
                          >
                            <span className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(ev) => {
                                  const next = ev.target.checked
                                    ? Array.from(
                                        new Set([
                                          ...(editSelectedDependentIds || []),
                                          id,
                                        ])
                                      )
                                    : (editSelectedDependentIds || []).filter(
                                        (x) => x !== id
                                      );
                                  setEditSelectedDependentIds(next);
                                }}
                              />
                              <span>
                                {d.firstName} {d.lastName}{" "}
                                <span className="text-muted-foreground">
                                  ({d.relationship})
                                </span>
                              </span>
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditEnrollment(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!editingEnrollment) return;
                      
                      // Validate waived coverage fields
                      if (editWaived) {
                        if (!editEventType) {
                          toast({ 
                            title: "Please select a reason for waiving", 
                            variant: "destructive" 
                          });
                          return;
                        }
                        if (!editEffectiveDate) {
                          toast({ 
                            title: "Please select a waived coverage start date", 
                            variant: "destructive" 
                          });
                          return;
                        }
                      }
                      
                      // Handle waiving vs normal update
                      try {
                        const wasNotWaived = !editingEnrollment.waivedCoverage;
                        const isBeingWaived = !!editWaived;
                        
                        // If transitioning from active → waived, CREATE new enrollment (don't update existing)
                        if (isBeingWaived && wasNotWaived) {
                          console.log('[Edit Enrollment] Creating new waived enrollment instead of updating');
                          
                          // Create NEW waived enrollment via POST
                          const response = await fetch(`/api/enrollments`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              employeeId: parseInt(employeeId || "0"),
                              planId: editingEnrollment.planId,
                              clientId: editingEnrollment.clientId,
                              coverageTier: editingEnrollment.coverageTier,
                              waivedCoverage: true,
                              effectiveDate: editEffectiveDate,
                              eventType: editEventType,
                              eventDescription: editEventDescription || null,
                              enrollmentSource: 'broker_manual',
                              // status will be set to 'pending' by backend for waived enrollments
                            }),
                          });
                          
                          if (!response.ok) {
                            const error = await response.json();
                            
                            toast({
                              title: "Unable to Waive Enrollment",
                              description: error.message || "Failed to create waived enrollment",
                              variant: "destructive",
                            });
                            
                            return;
                          }
                          
                          toast({
                            title: "Success",
                            description: "Enrollment waived successfully",
                          });
                          
                          // Invalidate queries and close dialog for waived enrollment
                          queryClientInstance.invalidateQueries({ queryKey: ["/api/enrollments"] });
                          setShowEditEnrollment(false);
                          setEditingEnrollment(null);
                          toast({ 
                            title: "Enrollment waived successfully. Old enrollment will end based on plan's termination timing.", 
                            variant: "success"
                          });
                          return; // Skip dependent sync for waived enrollments
                        } else {
                          // Normal update (not waiving or already waived)
                          await updateEnrollmentMutation.mutateAsync({
                            id: editingEnrollment.id,
                            waivedCoverage: !!editWaived,
                            effectiveDate: editWaived ? editEffectiveDate : undefined,
                            eventType: editWaived ? editEventType : null,
                            eventDescription: editWaived && editEventDescription ? editEventDescription : null,
                          });
                        }

                        // Sync dependent enrollments for this plan (only for non-waived updates).
                        // Treat waived dep rows as "not currently enrolled" — checkbox state
                        // on the dialog reflects active coverage only, so the diff must too.
                        const employeeNumericId = parseInt(employeeId || "0");
                        const res = await fetch(
                          `/api/enrollments?employeeId=${employeeNumericId}`
                        );
                        const rows = res.ok ? await res.json() : [];
                        const currentForPlan = (rows as any[]).filter((r) =>
                          isActiveDependentEnrollmentForPlan(
                            r,
                            editingEnrollment.planId,
                          ),
                        );
                        const existingSet = new Set(
                          currentForPlan.map((r) => r.dependentId as number)
                        );
                        const desiredSet = new Set(
                          editSelectedDependentIds || []
                        );

                        // Delete removed dependents
                        for (const r of currentForPlan.filter((r) => !desiredSet.has(r.dependentId as number))) {
                          const delRes = await fetch(`/api/enrollments/${r.id}`, { method: "DELETE" });
                          if (!delRes.ok && delRes.status !== 204) {
                            const err = await delRes.json().catch(() => ({}));
                            throw new Error(err.message || err.error || "Failed to remove dependent enrollment");
                          }
                        }
                        // Add new dependents
                        const toAdd = (editSelectedDependentIds || []).filter(
                          (id) => !existingSet.has(id)
                        );
                        for (const depId of toAdd) {
                          const addRes = await fetch(`/api/enrollments`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              employeeId: employeeNumericId,
                              clientId: employee?.clientId,
                              planId: editingEnrollment.planId,
                              dependentId: depId,
                              coverageTier: editingEnrollment.coverageTier,
                              effectiveDate: editingEnrollment.effectiveDate,
                              enrollmentSource: "update",
                            }),
                          });
                          if (!addRes.ok) {
                            const err = await addRes.json().catch(() => ({}));
                            throw new Error(err.message || err.error || "Failed to add dependent enrollment");
                          }
                        }
                        setShowEditEnrollment(false);
                        setEditingEnrollment(null);
                        // Reset edit state
                        setEditWaived(false);
                        setEditEventType("");
                        setEditEventDescription("");
                        queryClientInstance.invalidateQueries({
                          queryKey: ["/api/enrollments"],
                        });
                        toast({ title: "Enrollment updated" , variant: "success"});
                      } catch (updateError: any) {
                        console.error("[Edit Enrollment] Update failed:", updateError);
                        toast({
                          title: "Failed to Update Enrollment",
                          description: updateError.message || "An error occurred while updating the enrollment",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </Tabs>

      {/* Delete Employee Confirmation Dialog */}
      <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Employee?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {employee?.firstName} {employee?.lastName}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <p className="text-sm text-amber-900">
                <strong>Important:</strong> This employee can be deleted because they:
              </p>
              <ul className="list-disc list-inside text-sm text-amber-900 mt-2 space-y-1">
                <li>Have not been officially hired (no hire date)</li>
                <li>Have no benefit enrollments</li>
              </ul>
              <p className="text-sm text-amber-900 mt-2">
                This likely means they were added to the system but declined the job offer or never started employment.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteEmployeeMutation.mutate();
                setShowDeleteConfirmation(false);
              }}
              disabled={deleteEmployeeMutation.isPending}
            >
              {deleteEmployeeMutation.isPending ? "Deleting..." : "Delete Employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminate Employee Modal */}
      {employee && (
        <TerminateEmployeeModal
          open={showTerminateModal}
          onOpenChange={setShowTerminateModal}
          employee={{
            id: employee.id,
            firstName: employee.firstName || "",
            lastName: employee.lastName || "",
            hireDate: employee.hireDate || null,
            email: employee.email || undefined,
          }}
          activeEnrollments={activeEnrollmentsForTermination}
          onConfirm={handleTerminationConfirm}
          isProcessing={terminateMutation.isPending}
        />
      )}

      {/* Send Login Modal */}
      <Dialog
        open={showLoginModal}
        onOpenChange={(open) => {
          setShowLoginModal(open);
          if (!open) {
            setLoginResult(null);
            setLoginMode("reset_link");
            setLoginEmailUser(true);
            setLoginCopied(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Send login
            </DialogTitle>
            <DialogDescription>
              For {employee?.firstName} {employee?.lastName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Employee Email</label>
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-md border dark:border-gray-700 text-sm dark:text-gray-200">
                {employee?.email || <span className="text-gray-400 italic">No email on file</span>}
              </div>
              {!employee?.email && (
                <p className="text-xs text-amber-600">
                  Employee must have an email address to send login credentials.
                </p>
              )}
            </div>

            {loginResult ? (
              <div className="space-y-3">
                {loginResult.mode === "temp_password" && loginResult.tempPassword && (
                  <>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Temporary password
                    </label>
                    <div className="rounded-md border bg-gray-50 dark:bg-gray-800 p-3 font-mono text-sm break-all">
                      {loginResult.tempPassword}
                    </div>
                  </>
                )}
                {loginResult.mode === "reset_link" && loginResult.setupUrl && (
                  <>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Setup link</label>
                    <div className="rounded-md border bg-gray-50 dark:bg-gray-800 p-3 font-mono text-xs break-all">
                      {loginResult.setupUrl}
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {loginResult.emailSent
                      ? `Emailed to ${loginResult.email}.`
                      : "Not emailed — copy and share manually."}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const text = loginResult.tempPassword || loginResult.setupUrl;
                      if (!text) return;
                      try {
                        await navigator.clipboard.writeText(text);
                        setLoginCopied(true);
                      } catch {
                        // ignore
                      }
                    }}
                  >
                    {loginCopied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Method</label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 text-sm p-2 rounded border cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                      <input
                        type="radio"
                        name="loginMode"
                        className="mt-1"
                        checked={loginMode === "reset_link"}
                        onChange={() => setLoginMode("reset_link")}
                      />
                      <div>
                        <div className="font-medium">Send password reset link <span className="text-xs text-blue-600 ml-1">Recommended</span></div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          One-click setup link, expires in 7 days.
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 text-sm p-2 rounded border cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                      <input
                        type="radio"
                        name="loginMode"
                        className="mt-1"
                        checked={loginMode === "temp_password"}
                        onChange={() => setLoginMode("temp_password")}
                      />
                      <div>
                        <div className="font-medium">Generate temporary password</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          For users who can't use email links. They'll be required to change it on first login.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={loginEmailUser}
                    onChange={(e) => setLoginEmailUser(e.target.checked)}
                  />
                  Email it to {employee?.email || "the user"}
                </label>
              </>
            )}
          </div>

          <DialogFooter>
            {loginResult ? (
              <Button onClick={() => setShowLoginModal(false)}>Done</Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowLoginModal(false)}
                  disabled={sendLoginMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    sendLoginMutation.mutate({ mode: loginMode, sendEmail: loginEmailUser })
                  }
                  disabled={!employee?.email || sendLoginMutation.isPending}
                >
                  {sendLoginMutation.isPending
                    ? "Sending…"
                    : loginMode === "reset_link"
                      ? "Send link"
                      : "Generate"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employment Change Verification Modal */}
      <EmploymentVerificationModal />
    </div>
  );
}
